/**
 * @fileoverview Testing Page for Dev Environment
 *
 * Single-browser testing page that runs integration tests locally.
 * Organized by 5 categories: Movement, Capture, Status Effects, Bonus Turns, Game Over
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./TestingPage.module.css";
import {
  ALL_TEST_SUITES,
  ALL_MATCHMAKING_SUITES,
  runLocalTest,
  runMatchmakingTest,
  LocalTestResult,
  IntegrationTest,
  TestStep,
  flipSuite,
  MatchmakingTest,
  MatchmakingTestResult,
  MatchmakingTestSuite,
  runAITest,
  AITestResult,
  AITestConfig,
  AIFirebaseTestResult,
  AIValidationCheck,
  ALL_AI_DECISION_SUITES,
  AIDecisionTest,
  AIDecisionResult,
  runAIDecisionTest,
  flipAIDecisionTest,
  FORCED_LINE_TESTS,
  AIForcedLineTest,
  AIForcedLineResult,
  runAIForcedLineTest,
  executeAIMove,
  posToNotation,
} from "../../services/integrationTest";
import {
  runFirebaseTest,
  FirebaseTestResult,
  runAIFirebaseTest,
} from "../../services/integrationTest/firebase";
import {
  AIModel,
  TimeFrame,
  AI_MODEL_CONFIG,
  TIME_FRAME_CONFIG,
  getBaseAITypeFromModel,
  getTimeLimitFromFrame,
} from "../../services/zodiacEngine/AI/Core/AIVariants";
import { AIOrchestrator } from "../../services/zodiacEngine/AI/Core/AIOrchestrator";
import { AIPlayerType } from "../../services/zodiacEngine/AI/Core/IAIPlayer";
import { MinimaxAI } from "../../services/zodiacEngine/AI/MinimaxAI";
import Board from "../Board";
import { buildReplayGameState } from "../../services/zodiacEngine/Fen/FENDecoder";
import { GameContext } from "../Providers/GameContext";
import ZodiacEngineService from "../ZodiacEngineService";
import { loadTestScenarioFEN } from "../../services/zodiacEngine/Fen/FenLoader";
import { ZodiacFEN } from "../../services/zodiacEngine/Fen/ZodiacFEN";
import { State, Mode, GameState, IMove, ISideEffect, IPiece, Position } from "../../services/zodiacEngine/types";

type InputSource = "human" | "ai";
type Transport = "local" | "firebase";
type TestSide = "light" | "dark";

// Category definitions mapping to test suites
interface TestCategory {
  id: string;
  name: string;
  suiteName: string;
  description: string;
  isMatchmaking?: boolean;  // True if this runs Firebase service tests
  isAIDecision?: boolean;   // True if this runs AI decision tests
}

const TEST_CATEGORIES: TestCategory[] = [
  {
    id: "movement",
    name: "Piece Movement",
    suiteName: "Basic Movements",
    description: "Tests for standard piece movement across all 13 piece types",
  },
  {
    id: "special",
    name: "Special Movements",
    suiteName: "Special Movements",
    description: "Tests for special movement abilities (Lilypad, Traverse, Leap, Lane, etc.)",
  },
  {
    id: "capture",
    name: "Piece Capture",
    suiteName: "Captures",
    description: "Tests for piece captures including special capture rules",
  },
  {
    id: "status",
    name: "Status Effects",
    suiteName: "Status Effects",
    description: "Tests for poison, entangle, meadow, zombify, and other status effects",
  },
  {
    id: "bonus",
    name: "Bonus Turns",
    suiteName: "Bonus Turns",
    description: "Tests for bonus turn mechanics (Monkey, Virus, Penguin)",
  },
  {
    id: "delayed",
    name: "Delayed Effects",
    suiteName: "Delayed Effects",
    description: "Tests for effects that expire after turns (Poison, Zombify, Shadows)",
  },
  {
    id: "gameover",
    name: "Game Over",
    suiteName: "Game Over",
    description: "Tests for all victory and draw conditions - race victories, king captures, timeouts, stalemates, and repetition draws",
  },
  {
    id: "supercomplex",
    name: "Super Complex Scenarios",
    suiteName: "Super Complex Scenarios",
    description: "Tests for complex multi-piece interactions and edge cases",
  },
  {
    id: "matchmaking",
    name: "Matchmaking",
    suiteName: "Matchmaking",
    description: "Automated tests for matchmaking, ratings, and account management (runs against Firebase)",
    isMatchmaking: true,
  },
  {
    id: "clock",
    name: "Timer/Clock",
    suiteName: "Clock",
    description: "Tests for timer reset behavior between games, timeout handling, and preset switching",
    isMatchmaking: true,
  },
  {
    id: "ai-decisions",
    name: "AI Decisions",
    suiteName: "AI Decisions",
    description: "Tests that the AI picks the correct strategic move for tactical scenarios",
    isAIDecision: true,
  },
];

type TestStatus = "pending" | "passed" | "failed";

interface CategoryResults {
  [testName: string]: LocalTestResult;
}

interface AllResults {
  [categoryId: string]: CategoryResults;
}

// Matchmaking test results
interface MatchmakingResults {
  [testName: string]: MatchmakingTestResult;
}

// Interactive play mode exchange record
interface PlayModeExchange {
  exchangeNumber: number;
  humanMove?: string;        // "Light Bear D2->D1"
  humanMoveFEN: string;
  aiMove?: string;
  aiMoveFEN?: string;
  aiThinkingTimeMs?: number;
  aiReasoning?: string;
  rootMoveScores?: Array<{ description: string; score: number }>;
  gameOver?: boolean;
  gameResult?: string;
}

// Comparison view types
interface ComparisonStep {
  stepNumber: number;
  description: string;
  playerTurn: 'Light' | 'Dark';
  fen: string;
  absoluteValue?: number;
}

interface AIBranch {
  turnNumber: number;
  branchFEN: string;
  humanChoice?: string;
  aiFirstMove: string;
  aiPV: ComparisonStep[];
  aiResult: string;
  initialBoardValue?: number;
  priorHumanMoves: ComparisonStep[];
}

interface DivergenceReport {
  humanLine: ComparisonStep[];
  branches: AIBranch[];
  startingFEN: string;
  testName: string;
  humanResult: string;
}

/**
 * Step status icon component
 */
const StepIcon: React.FC<{ status: TestStep["status"] }> = ({ status }) => {
  switch (status) {
    case "passed":
      return <span className={styles.stepIconPassed}>&#10003;</span>;
    case "failed":
      return <span className={styles.stepIconFailed}>&#10007;</span>;
    case "skipped":
      return <span className={styles.stepIconSkipped}>&#8722;</span>;
    case "running":
      return <span className={styles.stepIconRunning}>&#8635;</span>;
    default:
      return <span className={styles.stepIconPending}>&#9675;</span>;
  }
};

const TestingPage: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [selectedCategory, setSelectedCategory] = useState<string>("movement");
  const [selectedTest, setSelectedTest] = useState<string | null>(null);
  const [results, setResults] = useState<AllResults>({});
  const [matchmakingResults, setMatchmakingResults] = useState<MatchmakingResults>({});
  const [isRunning, setIsRunning] = useState(false);
  const [currentRunningTest, setCurrentRunningTest] = useState<string | null>(null);
  const [isDarkPerspective, setIsDarkPerspective] = useState(false);
  const [testMode, setTestMode] = useState<Transport>("local");
  const [inputSource, setInputSource] = useState<InputSource>("human");
  const [aiModel, setAiModel] = useState<AIModel>(AIModel.CREATURE);
  const [aiTimeFrame, setAiTimeFrame] = useState<TimeFrame>(TimeFrame.FAST);

  // AI test results stored separately (different shape)
  const [aiResults, setAiResults] = useState<{ [categoryId: string]: { [testName: string]: AITestResult | AIFirebaseTestResult } }>({});

  // AI Decision test state
  const [aiDecisionModel, setAiDecisionModel] = useState<AIModel>(AIModel.SENTINEL);
  const [aiDecisionTimeFrame, setAiDecisionTimeFrame] = useState<TimeFrame>(TimeFrame.STANDARD);
  const [aiDecisionResults, setAiDecisionResults] = useState<{ [testName: string]: AIDecisionResult }>({});

  // Forced line test state
  const [forcedLineResults, setForcedLineResults] = useState<{ [testName: string]: AIForcedLineResult }>({});

  // Interactive Play vs AI state
  const [playModeActive, setPlayModeActive] = useState(false);
  const [playModeTest, setPlayModeTest] = useState<AIDecisionTest | null>(null);
  const [playModeGameState, setPlayModeGameState] = useState<GameState | null>(null);
  const [playModePotentialMoves, setPlayModePotentialMoves] = useState<IMove[]>([]);
  const [playModePotentialSideEffects, setPlayModePotentialSideEffects] = useState<ISideEffect[]>([]);
  const [playModeSelectedPiece, setPlayModeSelectedPiece] = useState<{ piece: IPiece; position: Position } | undefined>();
  const [playModeAIThinking, setPlayModeAIThinking] = useState(false);
  const [playModeHistory, setPlayModeHistory] = useState<PlayModeExchange[]>([]);
  const [playModeOrchestrator, setPlayModeOrchestrator] = useState<AIOrchestrator | null>(null);
  const [playModeHumanSide, setPlayModeHumanSide] = useState<'light' | 'dark'>('light');
  const [playModeGameOver, setPlayModeGameOver] = useState(false);
  const [playModeAIPlaysTestSide, setPlayModeAIPlaysTestSide] = useState(true); // AI plays the tested side by default
  const playModeLastTurnRef = useRef<{ isLightTurn: boolean; turnCount: number } | null>(null);

  // Divergence report state
  const [divergenceReport, setDivergenceReport] = useState<DivergenceReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportProgress, setReportProgress] = useState('');
  const [reportBoardFEN, setReportBoardFEN] = useState<string | null>(null);

  // PV board playback state
  const [pvStep, setPvStep] = useState(-1); // -1 = initial board, 0+ = after PV move N
  const [pvPlaying, setPvPlaying] = useState(false);
  const pvIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Reset PV playback when test selection changes
  useEffect(() => {
    setPvStep(-1);
    setPvPlaying(false);
    if (pvIntervalRef.current) clearInterval(pvIntervalRef.current);
  }, [selectedTest]);

  // Check if current category is matchmaking
  const currentCategory = useMemo(() => {
    return TEST_CATEGORIES.find((c) => c.id === selectedCategory);
  }, [selectedCategory]);

  const isMatchmakingCategory = currentCategory?.isMatchmaking ?? false;
  const isAIDecisionCategory = currentCategory?.isAIDecision ?? false;

  // Get AI decision suites (with flipped tests for Light perspective)
  const currentAIDecisionSuites = useMemo(() => {
    if (!isAIDecisionCategory) return [] as typeof ALL_AI_DECISION_SUITES;
    if (isDarkPerspective) return ALL_AI_DECISION_SUITES;
    return ALL_AI_DECISION_SUITES.map((suite) => ({
      ...suite,
      tests: suite.tests.map(flipAIDecisionTest),
    }));
  }, [isAIDecisionCategory, isDarkPerspective]);

  const currentAIDecisionTests = useMemo(() => {
    return currentAIDecisionSuites.flatMap((s) => s.tests);
  }, [currentAIDecisionSuites]);

  // Get the appropriate test suites based on perspective
  const activeSuites = useMemo(() => {
    if (!isDarkPerspective) return ALL_TEST_SUITES;
    return ALL_TEST_SUITES.map((suite) => flipSuite(suite));
  }, [isDarkPerspective]);

  // Get matchmaking tests for current category
  const currentMatchmakingTests = useMemo(() => {
    if (!isMatchmakingCategory) return [];
    const suite = ALL_MATCHMAKING_SUITES.find((s) => s.name === currentCategory?.suiteName);
    return suite?.tests || [];
  }, [isMatchmakingCategory, currentCategory]);

  // Get tests for selected category (engine tests only)
  const currentCategoryTests = useMemo(() => {
    const category = TEST_CATEGORIES.find((c) => c.id === selectedCategory);
    if (!category || category.isMatchmaking) return [];
    const suite = activeSuites.find((s) => s.name === category.suiteName || s.name === `${category.suiteName} (Dark Perspective)`);
    return suite?.tests || [];
  }, [selectedCategory, activeSuites]);

  // Get results for a category (handles engine, matchmaking, and AI decision tests)
  const getCategoryResults = useCallback(
    (categoryId: string) => {
      const category = TEST_CATEGORIES.find((c) => c.id === categoryId);
      if (!category) return { passed: 0, failed: 0, total: 0 };

      // Handle AI decision tests
      if (category.isAIDecision) {
        const allTests = ALL_AI_DECISION_SUITES.flatMap((s) => s.tests);
        let passed = 0;
        let failed = 0;

        allTests.forEach((test) => {
          const result = aiDecisionResults[test.name];
          if (result?.passed === true) passed++;
          else if (result?.passed === false) failed++;
        });

        return { passed, failed, total: allTests.length };
      }

      // Handle matchmaking tests
      if (category.isMatchmaking) {
        const suite = ALL_MATCHMAKING_SUITES.find((s) => s.name === category.suiteName);
        const tests = suite?.tests || [];
        let passed = 0;
        let failed = 0;

        tests.forEach((test) => {
          const result = matchmakingResults[test.name];
          if (result?.passed === true) passed++;
          else if (result?.passed === false) failed++;
        });

        return { passed, failed, total: tests.length };
      }

      // Handle engine tests (check both human and AI results)
      const categoryResults = results[categoryId] || {};
      const aiCategoryResults = aiResults[categoryId] || {};
      const suite = activeSuites.find((s) => s.name === category.suiteName || s.name === `${category.suiteName} (Dark Perspective)`);
      const tests = suite?.tests || [];

      let passed = 0;
      let failed = 0;

      tests.forEach((test) => {
        const humanResult = categoryResults[test.name];
        const aiResult = aiCategoryResults[test.name];
        const result = inputSource === "ai" ? aiResult : humanResult;
        if (result) {
          if (result.passed) passed++;
          else failed++;
        }
      });

      return { passed, failed, total: tests.length };
    },
    [results, aiResults, aiDecisionResults, matchmakingResults, activeSuites, inputSource]
  );

  // Run a matchmaking test
  const handleRunMatchmakingTest = useCallback(
    async (test: MatchmakingTest) => {
      setIsRunning(true);
      setCurrentRunningTest(test.name);

      await new Promise((resolve) => setTimeout(resolve, 50));

      const result = await runMatchmakingTest(test);

      setMatchmakingResults((prev) => ({
        ...prev,
        [test.name]: result,
      }));

      setIsRunning(false);
      setCurrentRunningTest(null);
      setSelectedTest(test.name);
    },
    []
  );

  // Run all matchmaking tests in current category
  const handleRunMatchmakingCategory = useCallback(async () => {
    setIsRunning(true);

    for (const test of currentMatchmakingTests) {
      setCurrentRunningTest(test.name);
      await new Promise((resolve) => setTimeout(resolve, 10));

      const result = await runMatchmakingTest(test);

      setMatchmakingResults((prev) => ({
        ...prev,
        [test.name]: result,
      }));
    }

    setIsRunning(false);
    setCurrentRunningTest(null);
  }, [currentMatchmakingTests]);

  // Get status for a specific test (handles engine, matchmaking, and AI decision)
  const getTestStatus = useCallback(
    (categoryId: string, testName: string): TestStatus => {
      const category = TEST_CATEGORIES.find((c) => c.id === categoryId);

      // Handle AI decision tests
      if (category?.isAIDecision) {
        const result = aiDecisionResults[testName];
        if (!result) return "pending";
        return result.passed ? "passed" : "failed";
      }

      // Handle matchmaking tests
      if (category?.isMatchmaking) {
        const result = matchmakingResults[testName];
        if (!result) return "pending";
        return result.passed ? "passed" : "failed";
      }

      // Handle engine tests (check based on input source)
      if (inputSource === "ai") {
        const aiCategoryResults = aiResults[categoryId];
        if (!aiCategoryResults) return "pending";
        const result = aiCategoryResults[testName];
        if (!result) return "pending";
        return result.passed ? "passed" : "failed";
      }
      const categoryResults = results[categoryId];
      if (!categoryResults) return "pending";
      const result = categoryResults[testName];
      if (!result) return "pending";
      return result.passed ? "passed" : "failed";
    },
    [results, aiResults, aiDecisionResults, matchmakingResults, inputSource]
  );

  // Run a single test (local or Firebase, human or AI)
  const handleRunTest = useCallback(
    async (test: IntegrationTest) => {
      setIsRunning(true);
      setCurrentRunningTest(test.name);

      // Small delay to show UI update
      await new Promise((resolve) => setTimeout(resolve, 50));

      if (inputSource === "ai") {
        const aiConfig: AITestConfig = {
          aiModel,
          timeFrame: aiTimeFrame,
          side: isDarkPerspective ? "dark" : "light",
        };
        let aiResult: AITestResult | AIFirebaseTestResult;
        if (testMode === "firebase") {
          aiResult = await runAIFirebaseTest(test, aiConfig);
        } else {
          aiResult = await runAITest(test, aiConfig);
        }
        setAiResults((prev) => ({
          ...prev,
          [selectedCategory]: {
            ...prev[selectedCategory],
            [test.name]: aiResult,
          },
        }));
      } else {
        let result: LocalTestResult | FirebaseTestResult;
        if (testMode === "firebase") {
          result = await runFirebaseTest(test);
        } else {
          result = await runLocalTest(test);
        }
        setResults((prev) => ({
          ...prev,
          [selectedCategory]: {
            ...prev[selectedCategory],
            [test.name]: result,
          },
        }));
      }

      setIsRunning(false);
      setCurrentRunningTest(null);
      setSelectedTest(test.name);
    },
    [selectedCategory, testMode, inputSource, aiModel, aiTimeFrame, isDarkPerspective]
  );

  // Run all tests in current category
  const handleRunCategory = useCallback(async () => {
    const category = TEST_CATEGORIES.find((c) => c.id === selectedCategory);
    if (!category) return;

    const suite = activeSuites.find((s) => s.name === category.suiteName || s.name === `${category.suiteName} (Dark Perspective)`);
    if (!suite) return;

    setIsRunning(true);

    for (const test of suite.tests) {
      setCurrentRunningTest(test.name);
      await new Promise((resolve) => setTimeout(resolve, 10));

      if (inputSource === "ai") {
        const aiConfig: AITestConfig = {
          aiModel,
          timeFrame: aiTimeFrame,
          side: isDarkPerspective ? "dark" : "light",
        };
        let aiResult: AITestResult | AIFirebaseTestResult;
        if (testMode === "firebase") {
          aiResult = await runAIFirebaseTest(test, aiConfig);
        } else {
          aiResult = await runAITest(test, aiConfig);
        }
        setAiResults((prev) => ({
          ...prev,
          [selectedCategory]: {
            ...prev[selectedCategory],
            [test.name]: aiResult,
          },
        }));
      } else {
        let result: LocalTestResult | FirebaseTestResult;
        if (testMode === "firebase") {
          result = await runFirebaseTest(test);
        } else {
          result = await runLocalTest(test);
        }
        setResults((prev) => ({
          ...prev,
          [selectedCategory]: {
            ...prev[selectedCategory],
            [test.name]: result,
          },
        }));
      }
    }

    setIsRunning(false);
    setCurrentRunningTest(null);
  }, [selectedCategory, activeSuites, testMode, inputSource, aiModel, aiTimeFrame, isDarkPerspective]);

  // Run all tests in all categories (including matchmaking)
  const handleRunAll = useCallback(async () => {
    setIsRunning(true);

    for (const category of TEST_CATEGORIES) {
      // Handle matchmaking tests separately
      if (category.isMatchmaking) {
        const matchmakingSuite = ALL_MATCHMAKING_SUITES.find((s) => s.name === category.suiteName);
        if (!matchmakingSuite) continue;

        for (const test of matchmakingSuite.tests) {
          setCurrentRunningTest(test.name);
          await new Promise((resolve) => setTimeout(resolve, 10));

          const result = await runMatchmakingTest(test);

          setMatchmakingResults((prev) => ({
            ...prev,
            [test.name]: result,
          }));
        }
        continue;
      }

      // Handle engine tests (local or firebase, human or AI)
      const suite = activeSuites.find((s) => s.name === category.suiteName || s.name === `${category.suiteName} (Dark Perspective)`);
      if (!suite) continue;

      for (const test of suite.tests) {
        setCurrentRunningTest(test.name);
        await new Promise((resolve) => setTimeout(resolve, 10));

        if (inputSource === "ai") {
          const aiConfig: AITestConfig = {
            aiModel,
            timeFrame: aiTimeFrame,
            side: isDarkPerspective ? "dark" : "light",
          };
          let aiResult: AITestResult | AIFirebaseTestResult;
          if (testMode === "firebase") {
            aiResult = await runAIFirebaseTest(test, aiConfig);
          } else {
            aiResult = await runAITest(test, aiConfig);
          }
          setAiResults((prev) => ({
            ...prev,
            [category.id]: {
              ...prev[category.id],
              [test.name]: aiResult,
            },
          }));
        } else {
          let result: LocalTestResult | FirebaseTestResult;
          if (testMode === "firebase") {
            result = await runFirebaseTest(test);
          } else {
            result = await runLocalTest(test);
          }
          setResults((prev) => ({
            ...prev,
            [category.id]: {
              ...prev[category.id],
              [test.name]: result,
            },
          }));
        }
      }
    }

    setIsRunning(false);
    setCurrentRunningTest(null);
  }, [activeSuites, testMode, inputSource, aiModel, aiTimeFrame, isDarkPerspective]);

  // Run a single AI decision test
  const handleRunAIDecisionTest = useCallback(
    async (test: AIDecisionTest) => {
      setIsRunning(true);
      setCurrentRunningTest(test.name);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const result = await runAIDecisionTest(test, aiDecisionModel, aiDecisionTimeFrame);

      setAiDecisionResults((prev) => ({
        ...prev,
        [test.name]: result,
      }));

      setIsRunning(false);
      setCurrentRunningTest(null);
      setSelectedTest(test.name);
    },
    [aiDecisionModel, aiDecisionTimeFrame]
  );

  // Run a forced line test
  const handleRunForcedLineTest = useCallback(
    async (test: AIForcedLineTest) => {
      setIsRunning(true);
      setCurrentRunningTest(test.name);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const result = await runAIForcedLineTest(test, aiDecisionModel, aiDecisionTimeFrame);

      setForcedLineResults((prev) => ({
        ...prev,
        [test.name]: result,
      }));

      setIsRunning(false);
      setCurrentRunningTest(null);
      setSelectedTest(test.name);
    },
    [aiDecisionModel, aiDecisionTimeFrame]
  );

  // Run all AI decision tests
  const handleRunAIDecisionCategory = useCallback(async () => {
    setIsRunning(true);

    for (const test of currentAIDecisionTests) {
      setCurrentRunningTest(test.name);
      await new Promise((resolve) => setTimeout(resolve, 10));

      const result = await runAIDecisionTest(test, aiDecisionModel, aiDecisionTimeFrame);

      setAiDecisionResults((prev) => ({
        ...prev,
        [test.name]: result,
      }));
    }

    setIsRunning(false);
    setCurrentRunningTest(null);
  }, [currentAIDecisionTests, aiDecisionModel, aiDecisionTimeFrame]);

  // === Interactive Play vs AI ===

  const handleEnterPlayMode = useCallback((test: AIDecisionTest) => {
    const engine = ZodiacEngineService.zodiacEngine;
    loadTestScenarioFEN(engine, test.inputFEN);

    // Force into playing state
    const gs = (engine as any).gameStateManager.getGameState();
    gs.state = State.Playing;
    gs.mode = Mode.TestAI;
    gs.turnCount = 10;

    // Determine sides: when "AI plays test side", the AI takes the test's side
    // and human plays opponent. Otherwise original behavior (human plays test side).
    const aiPlaysTestSide = playModeAIPlaysTestSide;
    const humanIsLight = aiPlaysTestSide
      ? (test.side !== 'light')  // AI plays test side → human plays opposite
      : (test.side === 'light'); // Human plays test side (original behavior)
    gs.isLightTurn = test.side === 'light'; // Test side always moves first

    // Configure AI and human sides
    const orchestrator = new AIOrchestrator(engine);
    const aiType = getBaseAITypeFromModel(aiDecisionModel);

    if (aiPlaysTestSide) {
      // AI plays the test side — lets you observe AI's actual decisions
      if (test.side === 'light') {
        orchestrator.setLightPlayer(aiType);
        orchestrator.setDarkPlayer(AIPlayerType.PLAYER);
      } else {
        orchestrator.setDarkPlayer(aiType);
        orchestrator.setLightPlayer(AIPlayerType.PLAYER);
      }
    } else {
      // Human plays the test side — original "prove you can do better" mode
      if (test.side === 'light') {
        orchestrator.setLightPlayer(AIPlayerType.PLAYER);
        orchestrator.setDarkPlayer(aiType);
      } else {
        orchestrator.setDarkPlayer(AIPlayerType.PLAYER);
        orchestrator.setLightPlayer(aiType);
      }
    }

    // Configure minimax model if applicable
    const modelConfig = AI_MODEL_CONFIG[aiDecisionModel];
    if (modelConfig.minimaxModel) {
      const minimaxAI = orchestrator.getAI(AIPlayerType.MINIMAX) as MinimaxAI;
      if (minimaxAI) minimaxAI.setModel(modelConfig.minimaxModel);
    }

    // Notify listeners so the engine state propagates
    if ((engine as any).eventManager) {
      (engine as any).eventManager.notifyLocalListeners();
    }

    // Store the initial turn state for change detection
    playModeLastTurnRef.current = { isLightTurn: gs.isLightTurn, turnCount: gs.turnCount };

    setPlayModeActive(true);
    setPlayModeTest(test);
    setPlayModeOrchestrator(orchestrator);
    setPlayModeHumanSide(humanIsLight ? 'light' : 'dark');
    setPlayModeGameState({ ...engine.getGameState() });
    setPlayModeHistory([]);
    setPlayModeGameOver(false);
    setPlayModePotentialMoves([]);
    setPlayModePotentialSideEffects([]);
    setPlayModeSelectedPiece(undefined);
    setPlayModeAIThinking(false);
  }, [aiDecisionModel, playModeAIPlaysTestSide]);

  const handleExitPlayMode = useCallback(() => {
    setPlayModeActive(false);
    setPlayModeTest(null);
    setPlayModeOrchestrator(null);
    setPlayModeHistory([]);
    setPlayModeGameOver(false);
    setPlayModeAIThinking(false);
    setPlayModePotentialMoves([]);
    setPlayModePotentialSideEffects([]);
    setPlayModeSelectedPiece(undefined);
    setDivergenceReport(null);
    setReportBoardFEN(null);
    playModeLastTurnRef.current = null;
  }, []);

  const handleGenerateReport = useCallback(async () => {
    if (!playModeTest || playModeHistory.length === 0) return;

    setReportLoading(true);
    setReportProgress('Building human line...');

    // 1. Build human line from playModeHistory (interleave human + AI opponent moves)
    const humanLine: ComparisonStep[] = [];
    for (const exchange of playModeHistory) {
      if (exchange.humanMove) {
        humanLine.push({
          stepNumber: humanLine.length + 1,
          description: exchange.humanMove,
          playerTurn: playModeHumanSide === 'light' ? 'Light' : 'Dark',
          fen: exchange.humanMoveFEN,
        });
      }
      if (exchange.aiMove && exchange.aiMoveFEN) {
        humanLine.push({
          stepNumber: humanLine.length + 1,
          description: exchange.aiMove,
          playerTurn: playModeHumanSide === 'light' ? 'Dark' : 'Light',
          fen: exchange.aiMoveFEN,
        });
      }
    }

    // 2. Collect branch point FENs (positions where it's the human/test-AI's turn)
    const branchPoints: { fen: string; humanChoice?: string }[] = [
      { fen: playModeTest.inputFEN, humanChoice: playModeHistory[0]?.humanMove },
    ];
    for (let i = 0; i < playModeHistory.length; i++) {
      if (playModeHistory[i].aiMoveFEN && i + 1 < playModeHistory.length) {
        branchPoints.push({
          fen: playModeHistory[i].aiMoveFEN!,
          humanChoice: playModeHistory[i + 1]?.humanMove,
        });
      }
    }

    // 3. Run AI from each branch point to get its PV
    const branches: AIBranch[] = [];
    for (let i = 0; i < branchPoints.length; i++) {
      setReportProgress(`Analyzing branch ${i + 1}/${branchPoints.length}...`);

      const bp = branchPoints[i];
      const syntheticTest: AIDecisionTest = {
        name: `Branch ${i}`,
        description: '',
        inputFEN: bp.fen,
        expectedDestination: { row: 0, col: 0 },
        side: playModeHumanSide,
        lightKing: playModeTest.lightKing,
        darkKing: playModeTest.darkKing,
      };

      const result = await runAIDecisionTest(syntheticTest, aiDecisionModel, aiDecisionTimeFrame);

      // Build PV steps
      const aiPV: ComparisonStep[] = (result.principalVariation || []).map((node: any, idx: number) => ({
        stepNumber: idx + 1,
        description: node.description || `Move ${idx + 1}`,
        playerTurn: node.playerTurn || (idx % 2 === 0
          ? (playModeHumanSide === 'light' ? 'Light' : 'Dark')
          : (playModeHumanSide === 'light' ? 'Dark' : 'Light')),
        fen: node.fen || '',
        absoluteValue: node.absoluteValue,
      }));

      // Determine AI result from terminal PV eval
      const lastNode = aiPV[aiPV.length - 1];
      const av = lastNode?.absoluteValue;
      const aiResultStr = av !== undefined
        ? (Math.abs(av) >= 100000
          ? (av >= 100000 ? 'LIGHT WINS' : 'DARK WINS')
          : `eval: ${av >= 0 ? '+' : ''}${av.toLocaleString()}`)
        : 'unknown';

      // AI's first move description
      const aiFirstMove = aiPV.length > 0 ? aiPV[0].description : '(no move)';

      branches.push({
        turnNumber: i,
        branchFEN: bp.fen,
        humanChoice: bp.humanChoice,
        aiFirstMove,
        aiPV,
        aiResult: aiResultStr,
        initialBoardValue: result.initialBoardValue,
        priorHumanMoves: humanLine.slice(0, i * 2),
      });
    }

    // 4. Determine human result
    const lastExchange = playModeHistory[playModeHistory.length - 1];
    const humanResult = lastExchange?.gameResult || 'In progress';

    setDivergenceReport({
      humanLine,
      branches,
      startingFEN: playModeTest.inputFEN,
      testName: playModeTest.name,
      humanResult,
    });
    setReportBoardFEN(null);
    setReportLoading(false);
    setReportProgress('');
  }, [playModeTest, playModeHistory, aiDecisionModel, aiDecisionTimeFrame, playModeHumanSide]);

  const triggerAIResponse = useCallback(async (humanMoveFEN: string, humanMoveDesc?: string) => {
    if (!playModeOrchestrator || playModeGameOver) return;

    setPlayModeAIThinking(true);

    const engine = ZodiacEngineService.zodiacEngine;
    const aiState = engine.getGameState();
    const timeLimit = getTimeLimitFromFrame(TimeFrame.STANDARD);

    // Check if game ended after human move
    if (aiState.state !== State.Playing) {
      const result = aiState.lightWon === true ? "Light wins" :
                     aiState.lightWon === false ? "Dark wins" : "Draw";
      setPlayModeHistory(prev => [...prev, {
        exchangeNumber: prev.length + 1,
        humanMove: humanMoveDesc,
        humanMoveFEN,
        gameOver: true,
        gameResult: result,
      }]);
      setPlayModeGameOver(true);
      setPlayModeAIThinking(false);
      return;
    }

    const aiThinkStart = performance.now();
    const moveResult = await playModeOrchestrator.requestAIMove(aiState, timeLimit);
    const aiThinkingTimeMs = performance.now() - aiThinkStart;

    if (!moveResult) {
      setPlayModeHistory(prev => [...prev, {
        exchangeNumber: prev.length + 1,
        humanMove: humanMoveDesc,
        humanMoveFEN,
        aiMove: "(no move returned)",
        aiThinkingTimeMs,
      }]);
      setPlayModeAIThinking(false);
      return;
    }

    // Build AI move description
    const fromSquare = aiState.board[moveResult.fromPosition.row]?.[moveResult.fromPosition.col];
    const aiPiece = fromSquare?.Pieces?.find((p: IPiece) => p.id === moveResult.move.pieceId);
    const aiColor = playModeHumanSide === 'light' ? "Dark" : "Light";
    const aiMoveDesc = `${aiColor} ${aiPiece?.type || '?'} ${posToNotation(moveResult.fromPosition)}->${posToNotation(moveResult.move.position)}`;

    // Execute AI move on the singleton engine
    await executeAIMove(engine, moveResult, aiState);

    const afterState = engine.getGameState();
    const aiMoveFEN = ZodiacFEN.encode(afterState);
    const gameOver = afterState.state !== State.Playing;
    const gameResult = gameOver
      ? (afterState.lightWon === true ? "Light wins" :
         afterState.lightWon === false ? "Dark wins" : "Draw")
      : undefined;

    // Update turn tracking ref after AI move
    playModeLastTurnRef.current = { isLightTurn: afterState.isLightTurn, turnCount: afterState.turnCount };

    // Update the game state for rendering
    setPlayModeGameState({ ...afterState });

    setPlayModeHistory(prev => [...prev, {
      exchangeNumber: prev.length + 1,
      humanMove: humanMoveDesc,
      humanMoveFEN,
      aiMove: aiMoveDesc,
      aiMoveFEN,
      aiReasoning: moveResult.reasoning,
      aiThinkingTimeMs,
      rootMoveScores: moveResult.rootMoveScores,
      gameOver,
      gameResult,
    }]);

    if (gameOver) {
      setPlayModeGameOver(true);
    }

    setPlayModeAIThinking(false);
  }, [playModeOrchestrator, playModeHumanSide, playModeGameOver]);

  // Engine state change listener for play mode
  useEffect(() => {
    if (!playModeActive || !playModeOrchestrator) return;

    const engine = ZodiacEngineService.zodiacEngine;

    const handleStateChange = () => {
      const gs = engine.getGameState();
      setPlayModeGameState({ ...gs });

      // Clear selection after any move
      setPlayModePotentialMoves([]);
      setPlayModePotentialSideEffects([]);
      setPlayModeSelectedPiece(undefined);

      // Detect if human just moved (turn changed from human's side to AI's side)
      const lastTurn = playModeLastTurnRef.current;
      if (!lastTurn) return;

      const wasHumansTurn = lastTurn.isLightTurn === (playModeHumanSide === 'light');
      const nowHumansTurn = gs.isLightTurn === (playModeHumanSide === 'light');
      const turnChanged = gs.turnCount !== lastTurn.turnCount ||
                          gs.isLightTurn !== lastTurn.isLightTurn;

      if (wasHumansTurn && !nowHumansTurn && turnChanged) {
        // Human just moved. Update tracking and trigger AI.
        playModeLastTurnRef.current = { isLightTurn: gs.isLightTurn, turnCount: gs.turnCount };
        const humanMoveFEN = ZodiacFEN.encode(gs);

        // Capture human move description from engine's lastMove
        let humanMoveDesc: string | undefined;
        const lm = (gs as any).lastMove;
        if (lm?.mover && lm.fromPosition && lm.toPosition) {
          const color = lm.mover.isLight ? 'Light' : 'Dark';
          humanMoveDesc = `${color} ${lm.mover.type} ${posToNotation(lm.fromPosition)}->${posToNotation(lm.toPosition)}`;
        }

        // Trigger AI response (don't await — it's async and updates state itself)
        triggerAIResponse(humanMoveFEN, humanMoveDesc);
      } else {
        // Update tracking for any other state change
        playModeLastTurnRef.current = { isLightTurn: gs.isLightTurn, turnCount: gs.turnCount };
      }
    };

    engine.subscribeToGameStateChanges(handleStateChange);
    return () => {
      engine.unsubscribeToGameStateChanges(handleStateChange);
    };
  }, [playModeActive, playModeOrchestrator, playModeHumanSide, triggerAIResponse]);

  // When AI plays the test side, it moves first — trigger AI immediately after entering play mode
  useEffect(() => {
    if (!playModeActive || !playModeOrchestrator || playModeGameOver) return;
    if (!playModeAIPlaysTestSide) return; // Only when AI plays test side

    const engine = ZodiacEngineService.zodiacEngine;
    const gs = engine.getGameState();
    const isAIsTurn = gs.isLightTurn !== (playModeHumanSide === 'light');

    // If it's AI's turn right at the start, trigger the first move
    if (isAIsTurn && playModeHistory.length === 0) {
      const initialFEN = ZodiacFEN.encode(gs);
      triggerAIResponse(initialFEN, '(AI moves first)');
    }
  }, [playModeActive, playModeOrchestrator, playModeAIPlaysTestSide, playModeHumanSide, playModeGameOver, playModeHistory.length, triggerAIResponse]);

  // Get selected AI decision test def and result
  const selectedAIDecisionTestDef = useMemo(() => {
    if (!selectedTest || !isAIDecisionCategory) return null;
    return currentAIDecisionTests.find((t) => t.name === selectedTest) || null;
  }, [selectedTest, currentAIDecisionTests, isAIDecisionCategory]);

  const selectedAIDecisionResult = useMemo(() => {
    if (!selectedTest || !isAIDecisionCategory) return null;
    return aiDecisionResults[selectedTest] || null;
  }, [selectedTest, aiDecisionResults, isAIDecisionCategory]);

  // Forced line test selection
  const selectedForcedLineTestDef = useMemo(() => {
    if (!selectedTest || !isAIDecisionCategory) return null;
    return FORCED_LINE_TESTS.find((t) => t.name === selectedTest) || null;
  }, [selectedTest, isAIDecisionCategory]);

  const selectedForcedLineResult = useMemo(() => {
    if (!selectedTest || !isAIDecisionCategory) return null;
    return forcedLineResults[selectedTest] || null;
  }, [selectedTest, forcedLineResults, isAIDecisionCategory]);

  // Auto-advance PV playback
  useEffect(() => {
    if (pvIntervalRef.current) clearInterval(pvIntervalRef.current);
    if (!pvPlaying) return;
    const pv = selectedAIDecisionResult?.principalVariation;
    if (!pv || pv.length === 0) return;
    pvIntervalRef.current = setInterval(() => {
      setPvStep(prev => {
        if (prev >= pv.length - 1) {
          setPvPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 500);
    return () => { if (pvIntervalRef.current) clearInterval(pvIntervalRef.current); };
  }, [pvPlaying, selectedAIDecisionResult]);

  // Get selected human test details (engine tests only — always LocalTestResult)
  const selectedTestResult = useMemo(() => {
    if (!selectedTest || inputSource === "ai") return null;
    return results[selectedCategory]?.[selectedTest] || null;
  }, [selectedTest, selectedCategory, results, inputSource]);

  // Get selected AI test result specifically (for AI details panel)
  const selectedAiResult = useMemo(() => {
    if (!selectedTest || inputSource !== "ai") return null;
    return aiResults[selectedCategory]?.[selectedTest] || null;
  }, [selectedTest, selectedCategory, aiResults, inputSource]);

  const selectedTestDef = useMemo(() => {
    if (!selectedTest || isMatchmakingCategory) return null;
    return currentCategoryTests.find((t) => t.name === selectedTest) || null;
  }, [selectedTest, currentCategoryTests, isMatchmakingCategory]);

  // Get selected matchmaking test details
  const selectedMatchmakingTestDef = useMemo(() => {
    if (!selectedTest || !isMatchmakingCategory) return null;
    return currentMatchmakingTests.find((t) => t.name === selectedTest) || null;
  }, [selectedTest, currentMatchmakingTests, isMatchmakingCategory]);

  const selectedMatchmakingTestResult = useMemo(() => {
    if (!selectedTest || !isMatchmakingCategory) return null;
    return matchmakingResults[selectedTest] || null;
  }, [selectedTest, matchmakingResults, isMatchmakingCategory]);

  // Calculate totals
  const totalStats = useMemo(() => {
    let passed = 0;
    let failed = 0;
    let total = 0;

    TEST_CATEGORIES.forEach((category) => {
      const stats = getCategoryResults(category.id);
      passed += stats.passed;
      failed += stats.failed;
      total += stats.total;
    });

    return { passed, failed, total };
  }, [getCategoryResults]);

  return (
    <div className={styles.container}>
      {/* Back Arrow */}
      <button className={styles.backArrow} onClick={() => navigate("/game")}>
        ←
      </button>

      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>
          Integration Tests
          <span className={styles.perspectiveLabel}>
            {inputSource === "ai" ? " [AI]" : " [Human]"}
            {testMode === "firebase" ? " Firebase" : " Local"}
            {isDarkPerspective ? " (Dark)" : " (Light)"}
          </span>
        </h1>
        <div className={styles.headerControls}>
          {/* Transport Toggle */}
          <button
            className={`${styles.modeToggle} ${testMode === "firebase" ? styles.firebaseMode : styles.localMode}`}
            onClick={() => {
              setTestMode(testMode === "local" ? "firebase" : "local");
              setResults({});
              setAiResults({});
              setSelectedTest(null);
            }}
            disabled={isRunning}
            title={testMode === "local" ? "Switch to Firebase P2P testing" : "Switch to Local testing"}
          >
            {testMode === "firebase" ? "Firebase P2P" : "Local"}
          </button>
          {/* Side Toggle */}
          <button
            className={`${styles.perspectiveToggle} ${isDarkPerspective ? styles.darkMode : styles.lightMode}`}
            onClick={() => {
              setIsDarkPerspective(!isDarkPerspective);
              setResults({});
              setAiResults({});
              setSelectedTest(null);
            }}
            disabled={isRunning}
          >
            {isDarkPerspective ? "Dark" : "Light"}
          </button>
          {/* AI Config Dropdowns - Only visible when AI input selected */}
          {inputSource === "ai" && (
            <>
              <select
                className={styles.aiSelect}
                value={aiModel}
                onChange={(e) => {
                  setAiModel(e.target.value as AIModel);
                  setAiResults({});
                  setSelectedTest(null);
                }}
                disabled={isRunning}
              >
                {Object.values(AI_MODEL_CONFIG).map((config) => (
                  <option key={config.model} value={config.model}>
                    {config.displayName}
                  </option>
                ))}
              </select>
              <select
                className={styles.aiSelect}
                value={aiTimeFrame}
                onChange={(e) => {
                  setAiTimeFrame(e.target.value as TimeFrame);
                  setAiResults({});
                  setSelectedTest(null);
                }}
                disabled={isRunning}
              >
                {Object.values(TIME_FRAME_CONFIG).map((config) => (
                  <option key={config.timeFrame} value={config.timeFrame}>
                    {config.displayName} ({config.timeLimit / 1000}s)
                  </option>
                ))}
              </select>
            </>
          )}
          <button className={styles.backButton} onClick={() => navigate("/game")}>
            Back to Dev
          </button>
        </div>
      </div>

      <div className={styles.content}>
        {/* Left Panel - Categories and Tests */}
        <div className={styles.leftPanel}>
          {/* Category Tabs */}
          <div className={styles.categoryTabs}>
            {TEST_CATEGORIES.map((category) => {
              const stats = getCategoryResults(category.id);
              return (
                <div
                  key={category.id}
                  className={`${styles.categoryTab} ${
                    selectedCategory === category.id ? styles.active : ""
                  }`}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setSelectedTest(null);
                    // Auto-switch to Firebase mode for matchmaking tests
                    if (category.isMatchmaking && testMode !== "firebase") {
                      setTestMode("firebase");
                      setResults({}); // Clear results when switching mode
                    }
                  }}
                >
                  <span className={styles.categoryName}>{category.name}</span>
                  <span className={styles.categoryCount}>
                    {stats.passed + stats.failed > 0 && (
                      <>
                        <span className={styles.passCount}>{stats.passed}</span>
                        <span>/</span>
                        <span className={styles.failCount}>{stats.failed}</span>
                        <span>/</span>
                      </>
                    )}
                    <span>{stats.total}</span>
                  </span>
                </div>
              );
            })}
          </div>

          {/* Test List - AI Decision Tests */}
          {isAIDecisionCategory && (
            <div className={styles.testList}>
              {currentAIDecisionSuites.map((suite) => (
                <React.Fragment key={suite.name}>
                  <div className={styles.testSuiteHeader}>{suite.name}</div>
                  {suite.tests.map((test) => {
                    const status = getTestStatus(selectedCategory, test.name);
                    return (
                      <div
                        key={test.name}
                        className={`${styles.testItem} ${
                          selectedTest === test.name ? styles.active : ""
                        }`}
                        onClick={() => setSelectedTest(test.name)}
                      >
                        <div className={`${styles.testStatus} ${styles[status]}`} />
                        <span className={styles.testName}>{test.name}</span>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
              {/* Forced Line Tests */}
              <div className={styles.testSuiteHeader}>Human Lines</div>
              {FORCED_LINE_TESTS.map((test) => {
                const hasResult = !!forcedLineResults[test.name];
                return (
                  <div
                    key={test.name}
                    className={`${styles.testItem} ${
                      selectedTest === test.name ? styles.active : ""
                    }`}
                    onClick={() => setSelectedTest(test.name)}
                  >
                    <div className={`${styles.testStatus} ${hasResult ? styles.passed : styles.pending}`} />
                    <span className={styles.testName}>{test.name}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Test List - Engine Tests */}
          {!isMatchmakingCategory && !isAIDecisionCategory && (
            <div className={styles.testList}>
              {currentCategoryTests.map((test) => {
                const status = getTestStatus(selectedCategory, test.name);
                return (
                  <div
                    key={test.name}
                    className={`${styles.testItem} ${
                      selectedTest === test.name ? styles.active : ""
                    }`}
                    onClick={() => setSelectedTest(test.name)}
                  >
                    <div className={`${styles.testStatus} ${styles[status]}`} />
                    <span className={styles.testName}>{test.name}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Test List - Matchmaking Tests */}
          {isMatchmakingCategory && (
            <div className={styles.testList}>
              {currentMatchmakingTests.map((test) => {
                const status = getTestStatus(selectedCategory, test.name);
                return (
                  <div
                    key={test.name}
                    className={`${styles.testItem} ${
                      selectedTest === test.name ? styles.active : ""
                    }`}
                    onClick={() => setSelectedTest(test.name)}
                  >
                    <div className={`${styles.testStatus} ${styles[status]}`} />
                    <span className={styles.testName}>{test.name}</span>
                    <span className={styles.testCategory}>[{test.category}]</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Panel - Details and Results */}
        <div className={styles.rightPanel}>
          {/* Controls - AI Decision Tests */}
          {isAIDecisionCategory && (
            <div className={styles.controls}>
              <select
                className={styles.aiSelect}
                value={aiDecisionModel}
                onChange={(e) => {
                  setAiDecisionModel(e.target.value as AIModel);
                  setAiDecisionResults({});
                  setSelectedTest(null);
                }}
                disabled={isRunning}
              >
                {Object.values(AI_MODEL_CONFIG).map((config) => (
                  <option key={config.model} value={config.model}>
                    {config.displayName}
                  </option>
                ))}
              </select>
              <select
                className={styles.aiSelect}
                value={aiDecisionTimeFrame}
                onChange={(e) => {
                  setAiDecisionTimeFrame(e.target.value as TimeFrame);
                  setAiDecisionResults({});
                  setSelectedTest(null);
                }}
                disabled={isRunning}
              >
                <option value={TimeFrame.FAST}>Fast (2s)</option>
                <option value={TimeFrame.STANDARD}>Standard (7s)</option>
                <option value={TimeFrame.DEEP}>Deep (15s)</option>
              </select>
              <button
                className={styles.runButton}
                onClick={handleRunAIDecisionCategory}
                disabled={isRunning}
              >
                Run All AI Tests
              </button>
              <button
                className={styles.runButton}
                onClick={() => {
                  if (selectedForcedLineTestDef) {
                    handleRunForcedLineTest(selectedForcedLineTestDef);
                  } else if (selectedAIDecisionTestDef) {
                    handleRunAIDecisionTest(selectedAIDecisionTestDef);
                  }
                }}
                disabled={isRunning || (!selectedAIDecisionTestDef && !selectedForcedLineTestDef)}
              >
                Run Selected
              </button>
              {selectedAIDecisionTestDef && !playModeActive && (
                <>
                  <button
                    className={styles.runButton}
                    onClick={() => setPlayModeAIPlaysTestSide(!playModeAIPlaysTestSide)}
                    disabled={isRunning}
                    style={{ background: playModeAIPlaysTestSide ? '#059669' : '#d97706', fontSize: '0.75rem', padding: '4px 8px' }}
                    title={playModeAIPlaysTestSide
                      ? 'AI plays the tested side — watch AI decisions'
                      : 'Human plays the tested side — prove you can do better'}
                  >
                    {playModeAIPlaysTestSide ? 'AI = Test Side' : 'Human = Test Side'}
                  </button>
                  <button
                    className={styles.runButton}
                    onClick={() => handleEnterPlayMode(selectedAIDecisionTestDef)}
                    disabled={isRunning}
                    style={{ background: '#6366f1' }}
                  >
                    Play vs AI
                  </button>
                </>
              )}
              {playModeActive && (
                <button
                  className={styles.runButton}
                  onClick={handleExitPlayMode}
                  style={{ background: '#ef4444' }}
                >
                  Exit Play Mode
                </button>
              )}
              {isRunning && (
                <div className={styles.runningIndicator}>
                  <div className={styles.spinner} />
                  <span>Running: {currentRunningTest}</span>
                </div>
              )}
            </div>
          )}

          {/* Controls - Engine Tests */}
          {!isMatchmakingCategory && !isAIDecisionCategory && (
            <div className={styles.controls}>
              <button
                className={styles.runButton}
                onClick={handleRunCategory}
                disabled={isRunning}
              >
                Run Category
              </button>
              <button
                className={`${styles.runButton} ${styles.runAllButton}`}
                onClick={handleRunAll}
                disabled={isRunning}
              >
                Run All Tests
              </button>
              {selectedTestDef && (
                <button
                  className={styles.runButton}
                  onClick={() => handleRunTest(selectedTestDef)}
                  disabled={isRunning}
                >
                  Run Selected
                </button>
              )}
              {isRunning && (
                <div className={styles.runningIndicator}>
                  <div className={styles.spinner} />
                  <span>Running: {currentRunningTest}</span>
                </div>
              )}
            </div>
          )}

          {/* Controls - Matchmaking Tests */}
          {isMatchmakingCategory && (
            <div className={styles.controls}>
              <button
                className={styles.runButton}
                onClick={handleRunMatchmakingCategory}
                disabled={isRunning}
              >
                Run Category
              </button>
              {selectedMatchmakingTestDef && (
                <button
                  className={styles.runButton}
                  onClick={() => handleRunMatchmakingTest(selectedMatchmakingTestDef)}
                  disabled={isRunning}
                >
                  Run Selected
                </button>
              )}
              {isRunning && (
                <div className={styles.runningIndicator}>
                  <div className={styles.spinner} />
                  <span>Running: {currentRunningTest}</span>
                </div>
              )}
            </div>
          )}

          {/* Summary Panel */}
          <div className={styles.summaryPanel}>
            <h3 className={styles.summaryTitle}>Test Summary</h3>
            <div className={styles.summaryStats}>
              <div className={styles.stat}>
                <div className={`${styles.statValue} ${styles.passed}`}>
                  {totalStats.passed}
                </div>
                <div className={styles.statLabel}>Passed</div>
              </div>
              <div className={styles.stat}>
                <div className={`${styles.statValue} ${styles.failed}`}>
                  {totalStats.failed}
                </div>
                <div className={styles.statLabel}>Failed</div>
              </div>
              <div className={styles.stat}>
                <div className={`${styles.statValue} ${styles.total}`}>
                  {totalStats.total}
                </div>
                <div className={styles.statLabel}>Total</div>
              </div>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{
                  width: `${
                    totalStats.passed + totalStats.failed > 0
                      ? (totalStats.passed / (totalStats.passed + totalStats.failed)) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>

          {/* Test Details Panel - Interactive Play vs AI */}
          {playModeActive && playModeGameState ? (
            <div className={styles.detailsPanel}>
              <h3 className={styles.detailsTitle}>
                Play vs AI: {playModeTest?.name}
                <span className={styles.aiLabel} style={{ marginLeft: 8 }}>
                  {playModeHumanSide === 'light' ? 'You: Light' : 'You: Dark'}
                </span>
              </h3>

              {/* Interactive Board */}
              <div className={styles.pvBoardContainer}>
                <div style={{
                  transform: 'scale(0.55)',
                  transformOrigin: 'top left',
                  width: 500,
                  height: 800,
                  marginBottom: -800 * (1 - 0.55),
                  marginRight: -500 * (1 - 0.55),
                }}>
                  <GameContext.Provider value={{
                    gameState: playModeGameState,
                    updateGameState: (gs: GameState) => setPlayModeGameState(gs),
                    selectedPiece: playModeSelectedPiece,
                    updateSelectedPiece: (sp: any) => setPlayModeSelectedPiece(sp),
                    potentialMoves: playModePotentialMoves,
                    updatePotentialMoves: (m: IMove[]) => setPlayModePotentialMoves(m),
                    potentialSideEffects: playModePotentialSideEffects,
                    updatePotentialSideEffects: (se: ISideEffect[]) => setPlayModePotentialSideEffects(se),
                    isThisPlayersTurn: () => !playModeAIThinking && !playModeGameOver,
                    isLightPlayer: () => playModeHumanSide === 'light',
                    selectionLocked: false,
                    setSelectionLocked: () => {},
                    lockSelection: () => {},
                    isAIThinking: playModeAIThinking,
                    setIsAIThinking: () => {},
                    isWatchMode: false,
                    setIsWatchMode: () => {},
                    isObserveMode: false,
                    lightPlayerGuess: null,
                    darkPlayerGuess: null,
                    setKingGuess: () => {},
                    clearKingGuess: () => {},
                    setBotKingGuess: () => {},
                    clearBotKingGuess: () => {},
                    clearAllKingGuesses: () => {},
                    getKingGuessForPiece: () => null,
                    gameId: null,
                    setGameId: () => {},
                    isSpectating: false,
                    setIsSpectating: () => {},
                    kingHighlightActive: false,
                    activateKingHighlight: () => {},
                  }}>
                    <Board
                      gameState={playModeGameState}
                      uiState={{ potentialMoves: playModePotentialMoves, potentialSideEffects: playModePotentialSideEffects }}
                      onDrop={() => {}}
                      onDragOver={() => {}}
                      flipBoard={playModeHumanSide === 'light'}
                    />
                  </GameContext.Provider>
                </div>
              </div>

              {/* Status */}
              <div style={{ textAlign: 'center', padding: '8px 0', fontSize: 14, fontWeight: 'bold', color: playModeAIThinking ? '#fbbf24' : playModeGameOver ? '#f87171' : '#4ade80' }}>
                {playModeAIThinking ? 'AI is thinking...' :
                 playModeGameOver ? `Game Over: ${playModeHistory[playModeHistory.length - 1]?.gameResult || 'Unknown'}` :
                 `Your turn (${playModeHumanSide})`}
              </div>

              {/* Divergence Report button */}
              {playModeHistory.length > 0 && !divergenceReport && (
                <div style={{ textAlign: 'center', padding: '4px 0' }}>
                  <button
                    className={styles.runButton}
                    onClick={handleGenerateReport}
                    disabled={reportLoading}
                    style={{ background: '#8b5cf6', fontSize: 13 }}
                  >
                    {reportLoading ? reportProgress || 'Generating...' : 'Divergence Report'}
                  </button>
                </div>
              )}

              {/* Divergence Report View */}
              {divergenceReport && (() => {
                const displayFEN = reportBoardFEN || divergenceReport.startingFEN;
                const boardState = buildReplayGameState(displayFEN, playModeTest?.lightKing || 'Bear', playModeTest?.darkKing || 'Bear', 0, true);
                const noop = () => {};
                return (
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <h4 className={styles.sectionTitle} style={{ margin: 0 }}>Divergence Report: {divergenceReport.testName}</h4>
                      <button
                        onClick={() => { setDivergenceReport(null); setReportBoardFEN(null); }}
                        style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#a1a1aa', cursor: 'pointer', padding: '4px 8px', borderRadius: 4, fontSize: 11 }}
                      >
                        Back
                      </button>
                    </div>

                    {/* Human Line (spine) */}
                    <div style={{ background: 'rgba(34,197,94,0.08)', borderRadius: 6, padding: 8, marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 'bold', color: '#4ade80', marginBottom: 6 }}>
                        Human Line (You)
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <span
                          onClick={() => setReportBoardFEN(null)}
                          style={{ cursor: 'pointer', fontSize: 11, fontFamily: 'monospace', padding: '2px 4px', borderRadius: 3,
                            color: '#71717a', background: !reportBoardFEN ? 'rgba(34,197,94,0.2)' : 'transparent' }}
                        >
                          Start
                        </span>
                        {divergenceReport.humanLine.map((step, idx) => {
                          const isSelected = reportBoardFEN === step.fen;
                          const turnColor = step.playerTurn === 'Light' ? '#fbbf24' : '#a78bfa';
                          return (
                            <span
                              key={idx}
                              onClick={() => setReportBoardFEN(step.fen)}
                              style={{ cursor: 'pointer', fontSize: 11, fontFamily: 'monospace', padding: '2px 4px', borderRadius: 3,
                                color: turnColor, background: isSelected ? 'rgba(34,197,94,0.25)' : 'transparent' }}
                            >
                              {step.stepNumber}. {step.description}
                            </span>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 'bold', marginTop: 4,
                        color: divergenceReport.humanResult.includes('Light wins') ? '#4ade80' : divergenceReport.humanResult.includes('Dark wins') ? '#f87171' : '#a1a1aa' }}>
                        {divergenceReport.humanResult}
                      </div>
                    </div>

                    {/* AI Branches */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 'bold', color: '#a78bfa', marginBottom: 6 }}>
                        AI Branches ({AI_MODEL_CONFIG[aiDecisionModel].displayName})
                      </div>
                      {divergenceReport.branches.map((branch) => {
                        const isWinning = branch.aiResult.includes('LIGHT WINS') === (playModeHumanSide === 'light');
                        const agrees = branch.humanChoice && branch.aiFirstMove === branch.humanChoice;
                        const evalColor = isWinning ? '#4ade80' : branch.aiResult.includes('WINS') ? '#f87171' : '#a1a1aa';
                        const borderColor = isWinning ? 'rgba(34,197,94,0.3)' : branch.aiResult.includes('WINS') ? 'rgba(248,113,113,0.3)' : 'rgba(255,255,255,0.1)';
                        return (
                          <div key={branch.turnNumber} style={{
                            marginBottom: 8, borderRadius: 6, border: `1px solid ${borderColor}`,
                            background: 'rgba(255,255,255,0.02)',
                          }}>
                            {/* Branch header */}
                            <div
                              onClick={() => setReportBoardFEN(branch.branchFEN)}
                              style={{ cursor: 'pointer', padding: '6px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                            >
                              <div style={{ fontSize: 11 }}>
                                <span style={{ color: '#71717a', fontWeight: 'bold', marginRight: 6 }}>
                                  {branch.turnNumber === 0 ? 'From Start' : `After Exchange ${branch.turnNumber}`}
                                </span>
                                {branch.initialBoardValue !== undefined && (
                                  <span style={{ color: evalColor, fontFamily: 'monospace', fontSize: 10 }}>
                                    (board: {branch.initialBoardValue >= 0 ? '+' : ''}{branch.initialBoardValue.toLocaleString()})
                                  </span>
                                )}
                                {agrees && (
                                  <span style={{ color: '#4ade80', marginLeft: 6, fontWeight: 'bold', fontSize: 10 }}>AGREES</span>
                                )}
                              </div>
                              <span style={{ color: evalColor, fontSize: 11, fontWeight: 'bold' }}>
                                {branch.aiResult}
                              </span>
                            </div>

                            <div style={{ padding: '6px 8px' }}>
                              {/* Prior human moves leading to this branch */}
                              {branch.priorHumanMoves.length > 0 && (
                                <div style={{ marginBottom: 6 }}>
                                  <div style={{ fontSize: 10, color: '#71717a', marginBottom: 2 }}>Human variation:</div>
                                  {branch.priorHumanMoves.map((step, idx) => {
                                    const isSelected = reportBoardFEN === step.fen;
                                    const turnColor = step.playerTurn === 'Light' ? '#fbbf24' : '#a78bfa';
                                    return (
                                      <div
                                        key={`prior-${idx}`}
                                        onClick={() => setReportBoardFEN(step.fen)}
                                        style={{
                                          padding: '1px 6px', cursor: 'pointer', borderRadius: 3, fontSize: 11, fontFamily: 'monospace',
                                          color: turnColor, opacity: 0.7,
                                          background: isSelected ? 'rgba(34,197,94,0.2)' : 'transparent',
                                          borderLeft: isSelected ? '2px solid #4ade80' : '2px solid transparent',
                                        }}
                                      >
                                        {step.description}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Human's choice at this branch point */}
                              <div style={{ fontSize: 11, fontFamily: 'monospace', marginBottom: 4 }}>
                                <span style={{ color: '#4ade80' }}>Human chose: {branch.humanChoice || '(end)'}</span>
                              </div>

                              {/* Full AI PV */}
                              <div style={{ marginBottom: 2 }}>
                                <div style={{ fontSize: 10, color: '#71717a', marginBottom: 2 }}>AI line:</div>
                                {branch.aiPV.map((step, idx) => {
                                  const isSelected = reportBoardFEN === step.fen;
                                  const turnColor = step.playerTurn === 'Light' ? '#fbbf24' : '#a78bfa';
                                  return (
                                    <div
                                      key={idx}
                                      onClick={() => setReportBoardFEN(step.fen)}
                                      style={{
                                        padding: '1px 6px', cursor: 'pointer', borderRadius: 3, fontSize: 11, fontFamily: 'monospace',
                                        color: turnColor,
                                        background: isSelected ? 'rgba(139,92,246,0.25)' : 'transparent',
                                        borderLeft: isSelected ? '2px solid #a78bfa' : '2px solid transparent',
                                      }}
                                    >
                                      {step.stepNumber}. {step.description}
                                      {step.absoluteValue !== undefined && (
                                        <span style={{ color: '#71717a', marginLeft: 4 }}>
                                          ({step.absoluteValue >= 0 ? '+' : ''}{step.absoluteValue.toLocaleString()})
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                                <div style={{ fontSize: 11, fontWeight: 'bold', marginTop: 2, padding: '1px 6px', color: evalColor }}>
                                  {branch.aiResult}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Shared Board */}
                    <div>
                      <div style={{ fontSize: 11, color: '#71717a', textAlign: 'center', marginBottom: 4 }}>
                        {reportBoardFEN ? 'Click any move to view position' : 'Starting Position'}
                      </div>
                      <div className={styles.pvBoardContainer}>
                        <div className={styles.pvBoardScaler}>
                          <GameContext.Provider value={{
                            gameState: boardState,
                            updateGameState: noop,
                            selectedPiece: undefined,
                            updateSelectedPiece: noop,
                            potentialMoves: [],
                            updatePotentialMoves: noop,
                            potentialSideEffects: [],
                            updatePotentialSideEffects: noop,
                            isThisPlayersTurn: () => false,
                            isLightPlayer: () => true,
                            selectionLocked: false,
                            setSelectionLocked: noop,
                            lockSelection: noop,
                            isAIThinking: false,
                            setIsAIThinking: noop,
                            isWatchMode: false,
                            setIsWatchMode: noop,
                            isObserveMode: false,
                            lightPlayerGuess: null,
                            darkPlayerGuess: null,
                            setKingGuess: noop,
                            clearKingGuess: noop,
                            setBotKingGuess: noop,
                            clearBotKingGuess: noop,
                            clearAllKingGuesses: noop,
                            getKingGuessForPiece: () => null,
                            gameId: null,
                            setGameId: noop,
                            isSpectating: false,
                            setIsSpectating: noop,
                            kingHighlightActive: false,
                            activateKingHighlight: noop,
                          }}>
                            <Board
                              gameState={boardState}
                              uiState={{ potentialMoves: [], potentialSideEffects: [] }}
                              onDrop={noop as any}
                              onDragOver={(e: any) => e.preventDefault()}
                              flipBoard={playModeHumanSide === 'light'}
                            />
                          </GameContext.Provider>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Exchange History (hidden when divergence report is active) */}
              {playModeHistory.length > 0 && !divergenceReport && (
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12 }}>
                  <h4 className={styles.sectionTitle}>Exchange History</h4>
                  {playModeHistory.map((exchange) => (
                    <div key={exchange.exchangeNumber} style={{ marginBottom: 16, padding: '8px', background: 'rgba(255,255,255,0.03)', borderRadius: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#c4b5fd', marginBottom: 6 }}>
                        Exchange {exchange.exchangeNumber}
                        {exchange.gameOver && (
                          <span style={{ marginLeft: 8, color: exchange.gameResult?.includes('Light') === (playModeHumanSide === 'light') ? '#4ade80' : '#f87171' }}>
                            {exchange.gameResult}
                          </span>
                        )}
                      </div>

                      {exchange.aiMove && (
                        <table className={styles.mmrTable} style={{ marginBottom: 8 }}>
                          <tbody>
                            <tr>
                              <td className={styles.mmrLabel}>AI Move:</td>
                              <td className={styles.mmrValue} style={{ color: '#fbbf24' }}>{exchange.aiMove}</td>
                            </tr>
                            {exchange.aiReasoning && (
                              <tr>
                                <td className={styles.mmrLabel}>Reasoning:</td>
                                <td className={styles.mmrValue}>{exchange.aiReasoning}</td>
                              </tr>
                            )}
                            <tr>
                              <td className={styles.mmrLabel}>Think Time:</td>
                              <td className={styles.mmrValue}>{exchange.aiThinkingTimeMs?.toFixed(0)}ms</td>
                            </tr>
                          </tbody>
                        </table>
                      )}

                      {/* Root Move Scores */}
                      {exchange.rootMoveScores && exchange.rootMoveScores.length > 0 && (
                        <div style={{ marginTop: 4 }}>
                          <div style={{ fontSize: 11, color: '#71717a', marginBottom: 4 }}>
                            AI Root Move Scores ({exchange.aiReasoning?.match(/Depth (\d+)/)?.[1] ? `depth ${exchange.aiReasoning.match(/Depth (\d+)/)?.[1]}` : 'minimax'}):
                          </div>
                          <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '12px', fontFamily: 'monospace' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#a1a1aa' }}>#</th>
                                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#a1a1aa' }}>Score</th>
                                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#a1a1aa' }}>Move</th>
                                </tr>
                              </thead>
                              <tbody>
                                {exchange.rootMoveScores.map((move, idx) => {
                                  const isChosen = idx === 0;
                                  const bgColor = isChosen ? 'rgba(99,102,241,0.2)' : 'transparent';
                                  const scoreColor = move.score > 1000 ? '#4ade80' : move.score < -1000 ? '#f87171' : '#e4e4e7';
                                  return (
                                    <tr key={idx} style={{ background: bgColor, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                      <td style={{ padding: '3px 8px', color: '#71717a' }}>{idx + 1}</td>
                                      <td style={{ padding: '3px 8px', color: scoreColor, fontWeight: isChosen ? 'bold' : 'normal' }}>
                                        {move.score.toLocaleString()}
                                      </td>
                                      <td style={{ padding: '3px 8px', color: isChosen ? '#c4b5fd' : '#d4d4d8' }}>
                                        {move.description}{isChosen ? ' *' : ''}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : selectedForcedLineTestDef ? (
            <div className={styles.detailsPanel}>
              <h3 className={styles.detailsTitle}>
                {selectedForcedLineTestDef.name}
                {selectedForcedLineResult && (
                  <span className={`${styles.statusBadge} ${styles.badgePassed}`}>
                    {selectedForcedLineResult.steps.length} STEP{selectedForcedLineResult.steps.length !== 1 ? 'S' : ''}
                  </span>
                )}
              </h3>

              <p className={styles.testDescription}>{selectedForcedLineTestDef.description}</p>

              {selectedForcedLineResult && (
                <>
                  {/* Error */}
                  {selectedForcedLineResult.error && (
                    <div className={styles.errorSection}>
                      <h4 className={styles.sectionTitle}>Error</h4>
                      <div className={styles.errorMessage}>{selectedForcedLineResult.error}</div>
                    </div>
                  )}

                  {/* Steps */}
                  {selectedForcedLineResult.steps.map((step) => (
                    <div key={step.stepNumber} className={styles.stepsSection}>
                      <h4 className={styles.sectionTitle}>
                        Step {step.stepNumber}
                        {step.gameOver && (
                          <span style={{ marginLeft: 8, color: step.gameResult?.includes('Light') ? '#4ade80' : '#f87171', fontWeight: 'bold' }}>
                            {step.gameResult}
                          </span>
                        )}
                      </h4>

                      {/* Move Pair */}
                      <table className={styles.mmrTable} style={{ marginBottom: 12 }}>
                        <tbody>
                          <tr>
                            <td className={styles.mmrLabel}>Human:</td>
                            <td className={styles.mmrValue} style={{ color: '#c4b5fd' }}>{step.humanMove}</td>
                          </tr>
                          <tr>
                            <td className={styles.mmrLabel}>AI:</td>
                            <td className={styles.mmrValue} style={{ color: '#fbbf24' }}>{step.aiMove}</td>
                          </tr>
                          {step.aiReasoning && (
                            <tr>
                              <td className={styles.mmrLabel}>Reasoning:</td>
                              <td className={styles.mmrValue}>{step.aiReasoning}</td>
                            </tr>
                          )}
                          <tr>
                            <td className={styles.mmrLabel}>Think Time:</td>
                            <td className={styles.mmrValue}>{step.aiThinkingTimeMs.toFixed(0)}ms</td>
                          </tr>
                        </tbody>
                      </table>

                      {/* Board after AI move */}
                      {step.aiMoveFEN && (() => {
                        const boardState = buildReplayGameState(
                          step.aiMoveFEN,
                          selectedForcedLineTestDef.lightKing || 'Bear',
                          selectedForcedLineTestDef.darkKing || 'Bear',
                          0,
                          true
                        );
                        const noop = () => {};
                        return (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 11, color: '#71717a', marginBottom: 4 }}>Board after step {step.stepNumber}:</div>
                            <GameContext.Provider value={{
                              gameState: boardState,
                              updateGameState: noop,
                              selectedPiece: undefined,
                              updateSelectedPiece: noop,
                              potentialMoves: [],
                              updatePotentialMoves: noop,
                              potentialSideEffects: [],
                              updatePotentialSideEffects: noop,
                              isThisPlayersTurn: () => false,
                              isLightPlayer: () => true,
                              selectionLocked: false,
                              setSelectionLocked: noop,
                              lockSelection: noop,
                              isAIThinking: false,
                              setIsAIThinking: noop,
                              isWatchMode: false,
                              setIsWatchMode: noop,
                              isObserveMode: false,
                              lightPlayerGuess: null,
                              darkPlayerGuess: null,
                              setKingGuess: noop,
                              clearKingGuess: noop,
                              setBotKingGuess: noop,
                              clearBotKingGuess: noop,
                              clearAllKingGuesses: noop,
                              getKingGuessForPiece: () => null,
                              gameId: null,
                              setGameId: noop,
                              isSpectating: false,
                              setIsSpectating: noop,
                              kingHighlightActive: false,
                              activateKingHighlight: noop,
                            }}>
                              <Board
                                gameState={boardState}
                                uiState={{ potentialMoves: [], potentialSideEffects: [] }}
                                onDrop={noop as any}
                                onDragOver={noop}
                                flipBoard={selectedForcedLineTestDef.side !== 'dark'}
                              />
                            </GameContext.Provider>
                          </div>
                        );
                      })()}

                      {/* Root Move Scores */}
                      {step.rootMoveScores && step.rootMoveScores.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, color: '#71717a', marginBottom: 4 }}>
                            AI Root Move Scores ({step.aiReasoning?.match(/Depth (\d+)/)?.[1] ? `depth ${step.aiReasoning.match(/Depth (\d+)/)?.[1]}` : 'minimax'}):
                          </div>
                          <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '12px', fontFamily: 'monospace' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#a1a1aa' }}>#</th>
                                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#a1a1aa' }}>Score</th>
                                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#a1a1aa' }}>Move</th>
                                </tr>
                              </thead>
                              <tbody>
                                {step.rootMoveScores.map((move, idx) => {
                                  const isChosen = idx === 0;
                                  const bgColor = isChosen ? 'rgba(99,102,241,0.2)' : 'transparent';
                                  const scoreColor = move.score > 1000 ? '#4ade80' : move.score < -1000 ? '#f87171' : '#e4e4e7';
                                  return (
                                    <tr key={idx} style={{ background: bgColor, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                      <td style={{ padding: '3px 8px', color: '#71717a' }}>{idx + 1}</td>
                                      <td style={{ padding: '3px 8px', color: scoreColor, fontWeight: isChosen ? 'bold' : 'normal' }}>
                                        {move.score.toLocaleString()}
                                      </td>
                                      <td style={{ padding: '3px 8px', color: isChosen ? '#c4b5fd' : '#d4d4d8' }}>
                                        {move.description}{isChosen ? ' *' : ''}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Final FEN */}
                  <div className={styles.fenSection}>
                    <h4 className={styles.sectionTitle}>Final Position</h4>
                    <div className={styles.fenGrid}>
                      <div className={styles.fenRow}>
                        <span className={styles.fenLabel}>FEN:</span>
                        <code className={styles.fenValue}>{selectedForcedLineResult.finalFEN}</code>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : selectedAIDecisionTestDef ? (
            <div className={styles.detailsPanel}>
              <h3 className={styles.detailsTitle}>
                {selectedAIDecisionTestDef.name}
                {selectedAIDecisionResult && (
                  <span
                    className={`${styles.statusBadge} ${
                      selectedAIDecisionResult.passed ? styles.badgePassed : styles.badgeFailed
                    }`}
                  >
                    {selectedAIDecisionResult.passed ? "PASSED" : "FAILED"}
                  </span>
                )}
                <span className={styles.aiLabel}>AI Decision</span>
              </h3>

              <p className={styles.testDescription}>{selectedAIDecisionTestDef.description}</p>

              <div className={styles.fenSection}>
                <div className={styles.fenRow}>
                  <span className={styles.fenLabel}>Input FEN:</span>
                  <code className={styles.fenValue}>{selectedAIDecisionTestDef.inputFEN}</code>
                </div>
                <div className={styles.fenRow}>
                  <span className={styles.fenLabel}>Side:</span>
                  <code className={styles.fenValue}>{selectedAIDecisionTestDef.side}</code>
                </div>
              </div>

              {selectedAIDecisionResult && (
                <>
                  {/* AI Move */}
                  <div className={styles.mmrSection}>
                    <h4 className={styles.sectionTitle}>AI Move</h4>
                    <table className={styles.mmrTable}>
                      <tbody>
                        <tr>
                          <td className={styles.mmrLabel}>From:</td>
                          <td className={styles.mmrValue}>
                            {selectedAIDecisionResult.actualFromPosition
                              ? `(${selectedAIDecisionResult.actualFromPosition.row}, ${selectedAIDecisionResult.actualFromPosition.col})`
                              : "(no move)"}
                          </td>
                        </tr>
                        {selectedAIDecisionTestDef.expectedDestination ? (
                        <tr>
                          <td className={styles.mmrLabel}>Expected To:</td>
                          <td className={styles.mmrValue}>
                            ({selectedAIDecisionTestDef.expectedDestination.row}, {selectedAIDecisionTestDef.expectedDestination.col})
                          </td>
                        </tr>
                        ) : selectedAIDecisionTestDef.forbiddenDestinations ? (
                        <tr>
                          <td className={styles.mmrLabel}>Forbidden:</td>
                          <td className={styles.mmrValue}>
                            {selectedAIDecisionTestDef.forbiddenDestinations.map(f => `(${f.row},${f.col})`).join(', ')}
                          </td>
                        </tr>
                        ) : null}
                        <tr>
                          <td className={styles.mmrLabel}>Actual To:</td>
                          <td className={`${styles.mmrValue} ${
                            selectedAIDecisionResult.actualDestination && (
                              selectedAIDecisionTestDef.expectedDestination
                                ? selectedAIDecisionResult.actualDestination.row === selectedAIDecisionTestDef.expectedDestination.row &&
                                  selectedAIDecisionResult.actualDestination.col === selectedAIDecisionTestDef.expectedDestination.col
                                : !selectedAIDecisionTestDef.forbiddenDestinations?.some(
                                    f => f.row === selectedAIDecisionResult.actualDestination!.row &&
                                         f.col === selectedAIDecisionResult.actualDestination!.col
                                  )
                            )
                              ? styles.positive : styles.negative
                          }`}>
                            {selectedAIDecisionResult.actualDestination
                              ? `(${selectedAIDecisionResult.actualDestination.row}, ${selectedAIDecisionResult.actualDestination.col})`
                              : "(no move)"}
                          </td>
                        </tr>
                        {selectedAIDecisionTestDef.expectedMoveType && (
                          <>
                            <tr>
                              <td className={styles.mmrLabel}>Expected Type:</td>
                              <td className={styles.mmrValue}>{selectedAIDecisionTestDef.expectedMoveType}</td>
                            </tr>
                            <tr>
                              <td className={styles.mmrLabel}>Actual Type:</td>
                              <td className={`${styles.mmrValue} ${
                                selectedAIDecisionResult.actualMoveType === selectedAIDecisionTestDef.expectedMoveType
                                  ? styles.positive : styles.negative
                              }`}>
                                {selectedAIDecisionResult.actualMoveType || "-"}
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Board State FENs */}
                  <div className={styles.fenSection}>
                    <h4 className={styles.sectionTitle}>Board State</h4>
                    <div className={styles.fenGrid}>
                      <div className={styles.fenRow}>
                        <span className={styles.fenLabel}>Before FEN:</span>
                        <code className={styles.fenValue}>{selectedAIDecisionResult.beforeFEN}</code>
                      </div>
                      <div className={styles.fenRow}>
                        <span className={styles.fenLabel}>After FEN:</span>
                        <code className={styles.fenValue}>{selectedAIDecisionResult.afterFEN}</code>
                      </div>
                    </div>
                  </div>

                  {/* AI Info */}
                  <div className={styles.mmrSection}>
                    <h4 className={styles.sectionTitle}>AI Info</h4>
                    <table className={styles.mmrTable}>
                      <tbody>
                        <tr>
                          <td className={styles.mmrLabel}>Model:</td>
                          <td className={styles.mmrValue}>{selectedAIDecisionResult.aiModel}</td>
                        </tr>
                        <tr>
                          <td className={styles.mmrLabel}>Thinking Time:</td>
                          <td className={styles.mmrValue}>{selectedAIDecisionResult.aiThinkingTimeMs.toFixed(0)}ms</td>
                        </tr>
                        {selectedAIDecisionResult.aiReasoning && (
                          <tr>
                            <td className={styles.mmrLabel}>Reasoning:</td>
                            <td className={styles.mmrValue}>{selectedAIDecisionResult.aiReasoning}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Principal Variation - Visual Board Playback */}
                  {selectedAIDecisionResult.principalVariation && selectedAIDecisionResult.principalVariation.length > 0 && (() => {
                    const pv = selectedAIDecisionResult.principalVariation!;
                    const currentFEN = pvStep === -1
                      ? selectedAIDecisionResult.beforeFEN
                      : (pv[pvStep]?.fen || selectedAIDecisionResult.beforeFEN);
                    const testDef = selectedAIDecisionTestDef;
                    const boardState = buildReplayGameState(
                      currentFEN,
                      testDef?.lightKing || 'Bear',
                      testDef?.darkKing || 'Bear',
                      Math.max(0, pvStep),
                      true
                    );
                    const currentNode = pvStep >= 0 ? pv[pvStep] : null;
                    const currentAV = currentNode?.absoluteValue;
                    const isTerminal = currentAV !== undefined && Math.abs(currentAV) >= 100000;
                    const noop = () => {};
                    return (
                      <div className={styles.mmrSection}>
                        <h4 className={styles.sectionTitle}>Principal Variation (Best Line)</h4>
                        {/* Board */}
                        <div className={styles.pvBoardContainer}>
                          <div className={styles.pvBoardScaler}>
                            <GameContext.Provider value={{
                              gameState: boardState,
                              updateGameState: noop,
                              selectedPiece: undefined,
                              updateSelectedPiece: noop,
                              potentialMoves: [],
                              updatePotentialMoves: noop,
                              potentialSideEffects: [],
                              updatePotentialSideEffects: noop,
                              isThisPlayersTurn: () => false,
                              isLightPlayer: () => true,
                              selectionLocked: false,
                              setSelectionLocked: noop,
                              lockSelection: noop,
                              isAIThinking: false,
                              setIsAIThinking: noop,
                              isWatchMode: false,
                              setIsWatchMode: noop,
                              isObserveMode: false,
                              lightPlayerGuess: null,
                              darkPlayerGuess: null,
                              setKingGuess: noop,
                              clearKingGuess: noop,
                              setBotKingGuess: noop,
                              clearBotKingGuess: noop,
                              clearAllKingGuesses: noop,
                              getKingGuessForPiece: () => null,
                              gameId: null,
                              setGameId: noop,
                              isSpectating: false,
                              setIsSpectating: noop,
                              kingHighlightActive: false,
                              activateKingHighlight: noop,
                            }}>
                              <Board
                                gameState={boardState}
                                uiState={{ potentialMoves: [], potentialSideEffects: [] }}
                                onDrop={noop as any}
                                onDragOver={noop}
                                flipBoard={testDef?.side !== 'dark'}
                              />
                            </GameContext.Provider>
                          </div>
                        </div>
                        {/* Current move description */}
                        <div style={{ textAlign: 'center', padding: '6px 0', fontFamily: "'Monaco', 'Menlo', monospace", fontSize: 12 }}>
                          {pvStep === -1 ? (
                            <span style={{ color: '#a1a1aa' }}>Initial position</span>
                          ) : currentNode ? (
                            <>
                              <span style={{ color: '#a1a1aa' }}>{pvStep + 1}/{pv.length} </span>
                              <span style={{ color: currentNode.playerTurn === 'Light' ? '#fbbf24' : '#a78bfa' }}>
                                {currentNode.description}
                              </span>
                              {isTerminal && (
                                <span style={{ color: '#FFD700', fontWeight: 'bold', marginLeft: 8 }}>
                                  {currentAV! >= 100000 ? 'LIGHT WINS' : 'DARK WINS'}
                                </span>
                              )}
                              {!isTerminal && currentAV !== undefined && (
                                <span style={{ color: '#71717a', marginLeft: 8 }}>
                                  AV: {currentAV >= 0 ? '+' : ''}{currentAV}
                                </span>
                              )}
                            </>
                          ) : null}
                        </div>
                        {/* Controls */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '4px 0' }}>
                          <button className={styles.pvControlBtn} onClick={() => { setPvStep(-1); setPvPlaying(false); }}>
                            Reset
                          </button>
                          <button className={styles.pvControlBtn} onClick={() => setPvStep(s => Math.max(-1, s - 1))} disabled={pvStep <= -1}>
                            Back
                          </button>
                          <button className={styles.pvControlBtn} onClick={() => {
                            if (pvStep >= pv.length - 1) {
                              setPvStep(-1);
                              setPvPlaying(true);
                            } else {
                              setPvPlaying(p => !p);
                            }
                          }}>
                            {pvPlaying ? 'Pause' : 'Play'}
                          </button>
                          <button className={styles.pvControlBtn} onClick={() => setPvStep(s => Math.min(pv.length - 1, s + 1))} disabled={pvStep >= pv.length - 1}>
                            Next
                          </button>
                        </div>
                        {/* Move list */}
                        <div style={{ fontFamily: "'Monaco', 'Menlo', monospace", fontSize: 11, background: 'rgba(0,0,0,0.3)', borderRadius: 4, padding: '6px 10px', marginTop: 4 }}>
                          {pv.map((node: any, idx: number) => {
                            const av = node.absoluteValue;
                            const term = av !== undefined && Math.abs(av) >= 100000;
                            return (
                              <div
                                key={idx}
                                onClick={() => { setPvStep(idx); setPvPlaying(false); }}
                                style={{
                                  padding: '2px 4px', cursor: 'pointer', borderRadius: 3,
                                  background: idx === pvStep ? 'rgba(99,102,241,0.3)' : 'transparent',
                                  borderBottom: idx < pv.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                }}
                              >
                                <span style={{ color: '#a1a1aa', marginRight: 4 }}>{idx + 1}.</span>
                                <span style={{ color: node.playerTurn === 'Light' ? '#fbbf24' : '#a78bfa' }}>
                                  {node.description}
                                </span>
                                {term && <span style={{ color: '#FFD700', fontWeight: 'bold', marginLeft: 6 }}>{av >= 100000 ? 'LIGHT WINS' : 'DARK WINS'}</span>}
                                {!term && av !== undefined && (
                                  <span style={{ color: '#71717a', marginLeft: 6 }}>AV: {av >= 0 ? '+' : ''}{av}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Root Move Scores */}
                  {selectedAIDecisionResult.rootMoveScores && selectedAIDecisionResult.rootMoveScores.length > 0 && (
                    <div className={styles.stepsSection}>
                      <h4 className={styles.sectionTitle}>Root Move Scores (depth {selectedAIDecisionResult.aiReasoning?.match(/Depth (\d+)/)?.[1] || '?'})</h4>
                      <div style={{ maxHeight: '300px', overflowY: 'auto', fontSize: '12px', fontFamily: 'monospace' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                              <th style={{ textAlign: 'left', padding: '4px 8px', color: '#a1a1aa' }}>#</th>
                              <th style={{ textAlign: 'left', padding: '4px 8px', color: '#a1a1aa' }}>Score</th>
                              <th style={{ textAlign: 'left', padding: '4px 8px', color: '#a1a1aa' }}>Move</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedAIDecisionResult.rootMoveScores.map((move, idx) => {
                              const isChosen = idx === 0;
                              const bgColor = isChosen ? 'rgba(99,102,241,0.2)' : 'transparent';
                              const scoreColor = move.score > 1000 ? '#4ade80' : move.score < -1000 ? '#f87171' : '#e4e4e7';
                              return (
                                <tr key={idx} style={{ background: bgColor, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                  <td style={{ padding: '3px 8px', color: '#71717a' }}>{idx + 1}</td>
                                  <td style={{ padding: '3px 8px', color: scoreColor, fontWeight: isChosen ? 'bold' : 'normal' }}>
                                    {move.score.toLocaleString()}
                                  </td>
                                  <td style={{ padding: '3px 8px', color: isChosen ? '#c4b5fd' : '#d4d4d8' }}>
                                    {move.description}{isChosen ? ' *' : ''}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Error */}
                  {selectedAIDecisionResult.error && (
                    <div className={styles.errorSection}>
                      <h4 className={styles.sectionTitle}>Error</h4>
                      <div className={styles.errorMessage}>{selectedAIDecisionResult.error}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : selectedMatchmakingTestDef ? (
            <div className={styles.detailsPanel}>
              <h3 className={styles.detailsTitle}>
                {selectedMatchmakingTestDef.name}
                {selectedMatchmakingTestResult && (
                  <span
                    className={`${styles.statusBadge} ${
                      selectedMatchmakingTestResult.passed ? styles.badgePassed : styles.badgeFailed
                    }`}
                  >
                    {selectedMatchmakingTestResult.passed ? "PASSED" : "FAILED"}
                  </span>
                )}
              </h3>

              <p className={styles.testDescription}>{selectedMatchmakingTestDef.description}</p>

              <div className={styles.behavioralCategory}>
                Category: <strong>{selectedMatchmakingTestDef.category.toUpperCase()}</strong>
              </div>

              {/* Test Result */}
              {selectedMatchmakingTestResult && (
                <div className={styles.stepsSection}>
                  <h4 className={styles.sectionTitle}>Result</h4>
                  <div className={styles.matchmakingResult}>
                    <div className={styles.resultMessage}>
                      {selectedMatchmakingTestResult.message}
                    </div>
                    {selectedMatchmakingTestResult.details && (
                      <div className={styles.resultDetails}>
                        {/* Rating Progression Section */}
                        {((selectedMatchmakingTestResult.details as any).ratingBefore !== undefined ||
                          (selectedMatchmakingTestResult.details as any).lightRatingBefore !== undefined) && (
                          <div className={styles.mmrSection}>
                            <h5 className={styles.mmrSectionTitle}>Rating Progression</h5>
                            <table className={styles.mmrTable}>
                              <tbody>
                                {(selectedMatchmakingTestResult.details as any).ratingBefore !== undefined && (
                                  <>
                                    <tr>
                                      <td className={styles.mmrLabel}>Initial Rating:</td>
                                      <td className={styles.mmrValue}>{(selectedMatchmakingTestResult.details as any).ratingBefore}</td>
                                    </tr>
                                    <tr>
                                      <td className={styles.mmrLabel}>Final Rating:</td>
                                      <td className={styles.mmrValue}>{(selectedMatchmakingTestResult.details as any).ratingAfter}</td>
                                    </tr>
                                    <tr>
                                      <td className={styles.mmrLabel}>Actual Change:</td>
                                      <td className={`${styles.mmrValue} ${
                                        String((selectedMatchmakingTestResult.details as any).actualChange).startsWith('+')
                                          ? styles.positive : styles.negative
                                      }`}>
                                        {(selectedMatchmakingTestResult.details as any).actualChange}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className={styles.mmrLabel}>Expected Change:</td>
                                      <td className={styles.mmrValue}>{(selectedMatchmakingTestResult.details as any).expectedChange}</td>
                                    </tr>
                                    <tr>
                                      <td className={styles.mmrLabel}>Calculation Match:</td>
                                      <td className={styles.mmrValue}>
                                        {(selectedMatchmakingTestResult.details as any).changeMatch ? "✓ Yes" : "✗ No"}
                                      </td>
                                    </tr>
                                  </>
                                )}
                                {(selectedMatchmakingTestResult.details as any).lightRatingBefore !== undefined && (
                                  <>
                                    <tr className={styles.mmrPlayerHeader}>
                                      <td colSpan={2}>Light Player (Loser)</td>
                                    </tr>
                                    <tr>
                                      <td className={styles.mmrLabel}>Rating:</td>
                                      <td className={styles.mmrValue}>
                                        {(selectedMatchmakingTestResult.details as any).lightRatingBefore} → {(selectedMatchmakingTestResult.details as any).lightRatingAfter}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className={styles.mmrLabel}>Change:</td>
                                      <td className={`${styles.mmrValue} ${styles.negative}`}>
                                        {(selectedMatchmakingTestResult.details as any).lightActualChange} (expected: {(selectedMatchmakingTestResult.details as any).lightExpectedChange})
                                      </td>
                                    </tr>
                                    <tr className={styles.mmrPlayerHeader}>
                                      <td colSpan={2}>Dark Player (Winner)</td>
                                    </tr>
                                    <tr>
                                      <td className={styles.mmrLabel}>Rating:</td>
                                      <td className={styles.mmrValue}>
                                        {(selectedMatchmakingTestResult.details as any).darkRatingBefore} → {(selectedMatchmakingTestResult.details as any).darkRatingAfter}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className={styles.mmrLabel}>Change:</td>
                                      <td className={`${styles.mmrValue} ${styles.positive}`}>
                                        {(selectedMatchmakingTestResult.details as any).darkActualChange} (expected: {(selectedMatchmakingTestResult.details as any).darkExpectedChange})
                                      </td>
                                    </tr>
                                  </>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Calculation Details Section */}
                        {(selectedMatchmakingTestResult.details as any).winProbability !== undefined && (
                          <div className={styles.mmrSection}>
                            <h5 className={styles.mmrSectionTitle}>Calculation Details</h5>
                            <table className={styles.mmrTable}>
                              <tbody>
                                <tr>
                                  <td className={styles.mmrLabel}>Opponent Rating:</td>
                                  <td className={styles.mmrValue}>{(selectedMatchmakingTestResult.details as any).opponentRating}</td>
                                </tr>
                                <tr>
                                  <td className={styles.mmrLabel}>Win Probability:</td>
                                  <td className={styles.mmrValue}>{(selectedMatchmakingTestResult.details as any).winProbability}</td>
                                </tr>
                                <tr>
                                  <td className={styles.mmrLabel}>Game Number:</td>
                                  <td className={styles.mmrValue}>{(selectedMatchmakingTestResult.details as any).gameNumber}</td>
                                </tr>
                                <tr>
                                  <td className={styles.mmrLabel}>Volatility (K-factor):</td>
                                  <td className={styles.mmrValue}>{(selectedMatchmakingTestResult.details as any).volatility}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Placement Status Section */}
                        {(selectedMatchmakingTestResult.details as any).placementProgress !== undefined && (
                          <div className={styles.mmrSection}>
                            <h5 className={styles.mmrSectionTitle}>Placement Status</h5>
                            <table className={styles.mmrTable}>
                              <tbody>
                                <tr>
                                  <td className={styles.mmrLabel}>Progress:</td>
                                  <td className={styles.mmrValue}>{(selectedMatchmakingTestResult.details as any).placementProgress}</td>
                                </tr>
                                <tr>
                                  <td className={styles.mmrLabel}>Placed:</td>
                                  <td className={styles.mmrValue}>
                                    {(selectedMatchmakingTestResult.details as any).isPlaced ? "✓ Yes" : "No (in placement)"}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* 10-game Journey Summary (check both direct and nested in actual for failures) */}
                        {((selectedMatchmakingTestResult.details as any).winLossRecord !== undefined ||
                          (selectedMatchmakingTestResult.details as any).actual?.winLossRecord !== undefined) && (
                          <div className={styles.mmrSection}>
                            <h5 className={styles.mmrSectionTitle}>Placement Summary</h5>
                            <table className={styles.mmrTable}>
                              <tbody>
                                {(() => {
                                  const d = (selectedMatchmakingTestResult.details as any).winLossRecord !== undefined
                                    ? (selectedMatchmakingTestResult.details as any)
                                    : (selectedMatchmakingTestResult.details as any).actual;
                                  return (
                                    <>
                                      <tr>
                                        <td className={styles.mmrLabel}>Record:</td>
                                        <td className={styles.mmrValue}>{d.winLossRecord}</td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Win Rate:</td>
                                        <td className={styles.mmrValue}>{d.winRate}</td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Start Rating:</td>
                                        <td className={styles.mmrValue}>{d.startRating}</td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Final Rating:</td>
                                        <td className={styles.mmrValue}>{d.finalRating}</td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Net Change:</td>
                                        <td className={`${styles.mmrValue} ${
                                          String(d.netChange).startsWith('+')
                                            ? styles.positive : styles.negative
                                        }`}>
                                          {d.netChange}
                                        </td>
                                      </tr>
                                    </>
                                  );
                                })()}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Firebase Profile Snapshot (for placement tests) */}
                        {((selectedMatchmakingTestResult.details as any).firebaseProfile ||
                          (selectedMatchmakingTestResult.details as any).actual?.firebaseProfile) && (
                          <div className={styles.mmrSection}>
                            <h5 className={styles.mmrSectionTitle}>Firebase Profile Snapshot</h5>
                            <table className={styles.mmrTable}>
                              <tbody>
                                {(() => {
                                  const profile = (selectedMatchmakingTestResult.details as any).firebaseProfile ||
                                    (selectedMatchmakingTestResult.details as any).actual?.firebaseProfile;
                                  return (
                                    <>
                                      <tr>
                                        <td className={styles.mmrLabel}>Provisional Rating:</td>
                                        <td className={styles.mmrValue}>{profile.provisionalRating}</td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Display Rating:</td>
                                        <td className={styles.mmrValue}>{profile.displayRating}</td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Placement Games:</td>
                                        <td className={styles.mmrValue}>{profile.placementGames}</td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Total Games:</td>
                                        <td className={styles.mmrValue}>{profile.totalGames}</td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Is Placed:</td>
                                        <td className={`${styles.mmrValue} ${profile.isPlaced ? styles.positive : styles.negative}`}>
                                          {profile.isPlaced ? "✓ Yes" : "✗ No"}
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Color Balance:</td>
                                        <td className={styles.mmrValue}>{profile.colorBalance}</td>
                                      </tr>
                                    </>
                                  );
                                })()}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Initial vs Final Profile Comparison (for placement tests) */}
                        {((selectedMatchmakingTestResult.details as any).initialProfile ||
                          (selectedMatchmakingTestResult.details as any).actual?.initialProfile) && (
                          <div className={styles.mmrSection}>
                            <h5 className={styles.mmrSectionTitle}>Profile Progression</h5>
                            <table className={styles.mmrTable}>
                              <tbody>
                                {(() => {
                                  const initial = (selectedMatchmakingTestResult.details as any).initialProfile ||
                                    (selectedMatchmakingTestResult.details as any).actual?.initialProfile;
                                  const final = (selectedMatchmakingTestResult.details as any).firebaseProfile ||
                                    (selectedMatchmakingTestResult.details as any).actual?.firebaseProfile;
                                  return (
                                    <>
                                      <tr>
                                        <td className={styles.mmrLabel}>Rating:</td>
                                        <td className={styles.mmrValue}>
                                          {initial?.provisionalRating} → {final?.provisionalRating || final?.displayRating}
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Placement Games:</td>
                                        <td className={styles.mmrValue}>
                                          {initial?.placementGames} → {final?.placementGames}
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className={styles.mmrLabel}>Placed Status:</td>
                                        <td className={styles.mmrValue}>
                                          {initial?.isPlaced ? "Yes" : "No"} → {final?.isPlaced ? "Yes" : "No"}
                                        </td>
                                      </tr>
                                    </>
                                  );
                                })()}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Show game-by-game breakdown if available (check both direct and nested in actual) */}
                        {(Array.isArray((selectedMatchmakingTestResult.details as any).games) ||
                          Array.isArray((selectedMatchmakingTestResult.details as any).actual?.games)) && (
                          <div className={styles.gamesBreakdown}>
                            <h5 className={styles.mmrSectionTitle}>Game-by-Game Breakdown</h5>
                            {(() => {
                              const games = ((selectedMatchmakingTestResult.details as any).games ||
                                (selectedMatchmakingTestResult.details as any).actual?.games) as any[];
                              // Check if games have expected/match columns (some tests don't)
                              const hasExpected = games.length > 0 && games[0].expected !== undefined;
                              const hasVsRating = games.length > 0 && games[0].vsRating !== undefined;
                              return (
                                <table className={styles.deltaTable}>
                                  <thead>
                                    <tr>
                                      <th>#</th>
                                      <th>Result</th>
                                      <th>Rating</th>
                                      <th>Change</th>
                                      {hasVsRating && <th>Vs Rating</th>}
                                      {hasExpected && <th>Expected</th>}
                                      {hasExpected && <th>Match</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {games.map((g: any) => (
                                      <tr key={g.game}>
                                        <td>{g.game}</td>
                                        <td className={g.result === "WIN" ? styles.passed : styles.failed}>
                                          {g.result}
                                        </td>
                                        <td>{g.rating}</td>
                                        <td className={g.change.startsWith('+') ? styles.positive : styles.negative}>
                                          {g.change}
                                        </td>
                                        {hasVsRating && <td>{g.vsRating}</td>}
                                        {hasExpected && <td>{g.expected}</td>}
                                        {hasExpected && <td>{g.match}</td>}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              );
                            })()}
                          </div>
                        )}

                        {/* Clock/Timer Test Data Display */}
                        {(selectedMatchmakingTestResult.details as any).clockTestData && (
                          <div className={styles.mmrSection}>
                            <h5 className={styles.mmrSectionTitle}>Timer Test Details</h5>
                            {(() => {
                              const clockData = (selectedMatchmakingTestResult.details as any).clockTestData;
                              return (
                                <>
                                  {/* Config Info */}
                                  {(clockData.preset || clockData.initialTime) && (
                                    <table className={styles.mmrTable}>
                                      <tbody>
                                        {clockData.preset && (
                                          <tr>
                                            <td className={styles.mmrLabel}>Time Control:</td>
                                            <td className={styles.mmrValue}>{clockData.preset}</td>
                                          </tr>
                                        )}
                                        {clockData.initialTime && (
                                          <tr>
                                            <td className={styles.mmrLabel}>Initial Time:</td>
                                            <td className={styles.mmrValue}>{clockData.initialTime}</td>
                                          </tr>
                                        )}
                                        {clockData.increment && (
                                          <tr>
                                            <td className={styles.mmrLabel}>Increment:</td>
                                            <td className={styles.mmrValue}>{clockData.increment}</td>
                                          </tr>
                                        )}
                                        {clockData.expectedInitialTime && (
                                          <tr>
                                            <td className={styles.mmrLabel}>Expected Initial:</td>
                                            <td className={styles.mmrValue}>{clockData.expectedInitialTime}</td>
                                          </tr>
                                        )}
                                        {clockData.iterations && (
                                          <tr>
                                            <td className={styles.mmrLabel}>Iterations:</td>
                                            <td className={styles.mmrValue}>{clockData.iterations}</td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  )}

                                  {/* Game 1 Data */}
                                  {clockData.game1 && (
                                    <div style={{ marginTop: 12 }}>
                                      <h6 style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                                        {clockData.game1.title || "Game 1"}
                                      </h6>
                                      <table className={styles.mmrTable}>
                                        <tbody>
                                          {clockData.game1.preset && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Preset:</td>
                                              <td className={styles.mmrValue}>{clockData.game1.preset}</td>
                                            </tr>
                                          )}
                                          {clockData.game1.initialTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Initial Time:</td>
                                              <td className={styles.mmrValue}>{clockData.game1.initialTime}</td>
                                            </tr>
                                          )}
                                          {clockData.game1.lightDeducted && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Light Deducted:</td>
                                              <td className={`${styles.mmrValue} ${styles.negative}`}>{clockData.game1.lightDeducted}</td>
                                            </tr>
                                          )}
                                          {clockData.game1.darkDeducted && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Dark Deducted:</td>
                                              <td className={`${styles.mmrValue} ${styles.negative}`}>{clockData.game1.darkDeducted}</td>
                                            </tr>
                                          )}
                                          {clockData.game1.deducted && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Time Deducted:</td>
                                              <td className={`${styles.mmrValue} ${styles.negative}`}>{clockData.game1.deducted}</td>
                                            </tr>
                                          )}
                                          {clockData.game1.lightEndTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Light End Time:</td>
                                              <td className={styles.mmrValue} style={{ color: '#f59e0b' }}>{clockData.game1.lightEndTime}</td>
                                            </tr>
                                          )}
                                          {clockData.game1.darkEndTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Dark End Time:</td>
                                              <td className={styles.mmrValue} style={{ color: '#f59e0b' }}>{clockData.game1.darkEndTime}</td>
                                            </tr>
                                          )}
                                          {clockData.game1.endTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>End Time:</td>
                                              <td className={styles.mmrValue} style={{ color: '#f59e0b' }}>{clockData.game1.endTime}</td>
                                            </tr>
                                          )}
                                          {clockData.game1.lightTimeAtEnd && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Light Time at End:</td>
                                              <td className={styles.mmrValue} style={{ color: '#ef4444' }}>{clockData.game1.lightTimeAtEnd}</td>
                                            </tr>
                                          )}
                                          {clockData.game1.timeExpired !== undefined && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Time Expired:</td>
                                              <td className={`${styles.mmrValue} ${clockData.game1.timeExpired ? styles.negative : styles.positive}`}>
                                                {clockData.game1.timeExpired ? "Yes" : "No"}
                                              </td>
                                            </tr>
                                          )}
                                          {clockData.game1.winner && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Winner:</td>
                                              <td className={styles.mmrValue}>{clockData.game1.winner}</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* Game 2 Data */}
                                  {clockData.game2 && (
                                    <div style={{ marginTop: 12 }}>
                                      <h6 style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                                        {clockData.game2.title || "Game 2"}
                                      </h6>
                                      <table className={styles.mmrTable}>
                                        <tbody>
                                          {clockData.game2.preset && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Preset:</td>
                                              <td className={styles.mmrValue}>{clockData.game2.preset}</td>
                                            </tr>
                                          )}
                                          {clockData.game2.expectedInitialTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Expected Initial:</td>
                                              <td className={styles.mmrValue}>{clockData.game2.expectedInitialTime}</td>
                                            </tr>
                                          )}
                                          {clockData.game2.lightStartTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Light Start Time:</td>
                                              <td className={`${styles.mmrValue} ${styles.positive}`}>{clockData.game2.lightStartTime}</td>
                                            </tr>
                                          )}
                                          {clockData.game2.darkStartTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Dark Start Time:</td>
                                              <td className={`${styles.mmrValue} ${styles.positive}`}>{clockData.game2.darkStartTime}</td>
                                            </tr>
                                          )}
                                          {clockData.game2.actualLightTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Actual Light Time:</td>
                                              <td className={`${styles.mmrValue} ${styles.positive}`}>{clockData.game2.actualLightTime}</td>
                                            </tr>
                                          )}
                                          {clockData.game2.actualDarkTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Actual Dark Time:</td>
                                              <td className={`${styles.mmrValue} ${styles.positive}`}>{clockData.game2.actualDarkTime}</td>
                                            </tr>
                                          )}
                                          {clockData.game2.timeExpiredFlag !== undefined && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Time Expired Flag:</td>
                                              <td className={`${styles.mmrValue} ${!clockData.game2.timeExpiredFlag ? styles.positive : styles.negative}`}>
                                                {clockData.game2.timeExpiredFlag ? "Yes (Error!)" : "No (Correct)"}
                                              </td>
                                            </tr>
                                          )}
                                          {clockData.game2.resetCorrect !== undefined && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Reset Correct:</td>
                                              <td className={`${styles.mmrValue} ${clockData.game2.resetCorrect ? styles.positive : styles.negative}`}>
                                                {clockData.game2.resetCorrect ? "Yes" : "No"}
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* Game Results Table (for rapid restart test) */}
                                  {clockData.gameResults && Array.isArray(clockData.gameResults) && (
                                    <div style={{ marginTop: 12 }}>
                                      <h6 style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                                        Game-by-Game Results
                                      </h6>
                                      <table className={styles.deltaTable}>
                                        <thead>
                                          <tr>
                                            <th>Game</th>
                                            <th>Start Time</th>
                                            <th>Deducted</th>
                                            <th>End Time</th>
                                            <th>Reset OK</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {clockData.gameResults.map((g: any) => (
                                            <tr key={g.game}>
                                              <td>{g.game}</td>
                                              <td>{g.startTime}</td>
                                              <td className={styles.negative}>{g.deducted}</td>
                                              <td style={{ color: '#f59e0b' }}>{g.endTime}</td>
                                              <td className={g.correct === "Yes" ? styles.positive : styles.negative}>
                                                {g.correct}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* Final Game (for rapid restart test) */}
                                  {clockData.finalGame && (
                                    <div style={{ marginTop: 12 }}>
                                      <h6 style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                                        {clockData.finalGame.title || "Final Verification"}
                                      </h6>
                                      <table className={styles.mmrTable}>
                                        <tbody>
                                          {clockData.finalGame.lightTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Light Time:</td>
                                              <td className={`${styles.mmrValue} ${styles.positive}`}>{clockData.finalGame.lightTime}</td>
                                            </tr>
                                          )}
                                          {clockData.finalGame.darkTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Dark Time:</td>
                                              <td className={`${styles.mmrValue} ${styles.positive}`}>{clockData.finalGame.darkTime}</td>
                                            </tr>
                                          )}
                                          {clockData.finalGame.allResetsCorrect !== undefined && (
                                            <tr>
                                              <td className={styles.mmrLabel}>All Resets Correct:</td>
                                              <td className={`${styles.mmrValue} ${clockData.finalGame.allResetsCorrect ? styles.positive : styles.negative}`}>
                                                {clockData.finalGame.allResetsCorrect ? "Yes" : "No"}
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* Before Game End State (for timer stop test) */}
                                  {clockData.beforeGameEnd && (
                                    <div style={{ marginTop: 12 }}>
                                      <h6 style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                                        {clockData.beforeGameEnd.title || "Before Game End"}
                                      </h6>
                                      <table className={styles.mmrTable}>
                                        <tbody>
                                          {clockData.beforeGameEnd.isPaused !== undefined && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Is Paused:</td>
                                              <td className={`${styles.mmrValue} ${clockData.beforeGameEnd.isPaused ? styles.negative : styles.positive}`}>
                                                {clockData.beforeGameEnd.isPaused ? "Yes (paused)" : "No (running)"}
                                              </td>
                                            </tr>
                                          )}
                                          {clockData.beforeGameEnd.activePlayer && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Active Player:</td>
                                              <td className={styles.mmrValue}>{clockData.beforeGameEnd.activePlayer}</td>
                                            </tr>
                                          )}
                                          {clockData.beforeGameEnd.wasRunning && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Was Running:</td>
                                              <td className={`${styles.mmrValue} ${clockData.beforeGameEnd.wasRunning === "Yes" ? styles.positive : styles.negative}`}>
                                                {clockData.beforeGameEnd.wasRunning}
                                              </td>
                                            </tr>
                                          )}
                                          {clockData.beforeGameEnd.lightTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Light Time:</td>
                                              <td className={styles.mmrValue}>{clockData.beforeGameEnd.lightTime}</td>
                                            </tr>
                                          )}
                                          {clockData.beforeGameEnd.darkTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Dark Time:</td>
                                              <td className={styles.mmrValue}>{clockData.beforeGameEnd.darkTime}</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* After Game End State (for timer stop test) */}
                                  {clockData.afterGameEnd && (
                                    <div style={{ marginTop: 12 }}>
                                      <h6 style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                                        {clockData.afterGameEnd.title || "After Game End"}
                                      </h6>
                                      <table className={styles.mmrTable}>
                                        <tbody>
                                          {clockData.afterGameEnd.isPaused !== undefined && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Is Paused:</td>
                                              <td className={`${styles.mmrValue} ${clockData.afterGameEnd.isPaused ? styles.positive : styles.negative}`}>
                                                {clockData.afterGameEnd.isPaused ? "Yes (stopped)" : "No (still running!)"}
                                              </td>
                                            </tr>
                                          )}
                                          {clockData.afterGameEnd.activePlayer && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Active Player:</td>
                                              <td className={styles.mmrValue}>{clockData.afterGameEnd.activePlayer}</td>
                                            </tr>
                                          )}
                                          {clockData.afterGameEnd.correctlyStopped && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Correctly Stopped:</td>
                                              <td className={`${styles.mmrValue} ${clockData.afterGameEnd.correctlyStopped === "Yes" ? styles.positive : styles.negative}`}>
                                                {clockData.afterGameEnd.correctlyStopped}
                                              </td>
                                            </tr>
                                          )}
                                          {clockData.afterGameEnd.lightTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Light Time:</td>
                                              <td className={styles.mmrValue}>{clockData.afterGameEnd.lightTime}</td>
                                            </tr>
                                          )}
                                          {clockData.afterGameEnd.darkTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Dark Time:</td>
                                              <td className={styles.mmrValue}>{clockData.afterGameEnd.darkTime}</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* Before Timeout State (for timeout test) */}
                                  {clockData.beforeTimeout && (
                                    <div style={{ marginTop: 12 }}>
                                      <h6 style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                                        {clockData.beforeTimeout.title || "Before Timeout"}
                                      </h6>
                                      <table className={styles.mmrTable}>
                                        <tbody>
                                          {clockData.beforeTimeout.lightTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Light Time:</td>
                                              <td className={styles.mmrValue}>{clockData.beforeTimeout.lightTime}</td>
                                            </tr>
                                          )}
                                          {clockData.beforeTimeout.darkTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Dark Time:</td>
                                              <td className={styles.mmrValue}>{clockData.beforeTimeout.darkTime}</td>
                                            </tr>
                                          )}
                                          {clockData.beforeTimeout.timeExpired && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Time Expired:</td>
                                              <td className={`${styles.mmrValue} ${clockData.beforeTimeout.timeExpired === "No" ? styles.positive : styles.negative}`}>
                                                {clockData.beforeTimeout.timeExpired}
                                              </td>
                                            </tr>
                                          )}
                                          {clockData.beforeTimeout.winner && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Winner:</td>
                                              <td className={styles.mmrValue}>{clockData.beforeTimeout.winner}</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* After Timeout State (for timeout test) */}
                                  {clockData.afterTimeout && (
                                    <div style={{ marginTop: 12 }}>
                                      <h6 style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                                        {clockData.afterTimeout.title || "After Timeout"}
                                      </h6>
                                      <table className={styles.mmrTable}>
                                        <tbody>
                                          {clockData.afterTimeout.lightTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Light Time:</td>
                                              <td className={`${styles.mmrValue} ${styles.negative}`}>{clockData.afterTimeout.lightTime}</td>
                                            </tr>
                                          )}
                                          {clockData.afterTimeout.darkTime && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Dark Time:</td>
                                              <td className={styles.mmrValue}>{clockData.afterTimeout.darkTime}</td>
                                            </tr>
                                          )}
                                          {clockData.afterTimeout.timeExpired && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Time Expired:</td>
                                              <td className={`${styles.mmrValue} ${clockData.afterTimeout.timeExpired === "Yes" ? styles.positive : styles.negative}`}>
                                                {clockData.afterTimeout.timeExpired}
                                              </td>
                                            </tr>
                                          )}
                                          {clockData.afterTimeout.winner && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Winner:</td>
                                              <td className={`${styles.mmrValue} ${styles.positive}`}>{clockData.afterTimeout.winner}</td>
                                            </tr>
                                          )}
                                          {clockData.afterTimeout.correctWinner && (
                                            <tr>
                                              <td className={styles.mmrLabel}>Correct Winner:</td>
                                              <td className={`${styles.mmrValue} ${clockData.afterTimeout.correctWinner.startsWith("Yes") ? styles.positive : styles.negative}`}>
                                                {clockData.afterTimeout.correctWinner}
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}

                        {/* Error display */}
                        {selectedMatchmakingTestResult.details.error && (
                          <div className={styles.errorSection}>
                            <h4 className={styles.sectionTitle}>Error</h4>
                            <div className={styles.errorMessage}>
                              {selectedMatchmakingTestResult.details.error}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timing and Cleanup Info */}
              {selectedMatchmakingTestResult && (
                <div className={styles.timingSection}>
                  <span className={styles.timingLabel}>Duration:</span>
                  <span className={styles.timingValue}>
                    {selectedMatchmakingTestResult.duration.toFixed(2)}ms
                  </span>
                  {(selectedMatchmakingTestResult.details as any)?._testAccountsCleaned !== undefined && (
                    <>
                      <span className={styles.timingLabel} style={{ marginLeft: 16 }}>Test Accounts:</span>
                      <span className={styles.timingValue} style={{ color: '#4ade80' }}>
                        {(selectedMatchmakingTestResult.details as any)._testAccountsCleaned} cleaned up
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (selectedAiResult && selectedTestDef) ? (
            /* AI Test Results Panel */
            <div className={styles.detailsPanel}>
              <h3 className={styles.detailsTitle}>
                {selectedTestDef.name}
                <span
                  className={`${styles.statusBadge} ${
                    selectedAiResult.passed ? styles.badgePassed : styles.badgeFailed
                  }`}
                >
                  {selectedAiResult.passed ? "PASSED" : "FAILED"}
                </span>
                <span className={styles.aiLabel}>AI {selectedAiResult._type === "ai-firebase" ? "Firebase" : "Local"}</span>
              </h3>

              {selectedTestDef.description && (
                <p className={styles.testDescription}>{selectedTestDef.description}</p>
              )}

              {/* AI Move Info */}
              <div className={styles.mmrSection}>
                <h4 className={styles.sectionTitle}>AI Move</h4>
                <table className={styles.mmrTable}>
                  <tbody>
                    <tr>
                      <td className={styles.mmrLabel}>Move Made:</td>
                      <td className={styles.mmrValue}>{selectedAiResult.aiMoveMade || "(none)"}</td>
                    </tr>
                    <tr>
                      <td className={styles.mmrLabel}>Thinking Time:</td>
                      <td className={styles.mmrValue}>{selectedAiResult.aiThinkingTimeMs.toFixed(0)}ms</td>
                    </tr>
                    <tr>
                      <td className={styles.mmrLabel}>Confidence:</td>
                      <td className={styles.mmrValue}>{(selectedAiResult.aiConfidence * 100).toFixed(1)}%</td>
                    </tr>
                    {selectedAiResult.aiReasoning && (
                      <tr>
                        <td className={styles.mmrLabel}>Reasoning:</td>
                        <td className={styles.mmrValue}>{selectedAiResult.aiReasoning}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* FEN Before/After */}
              <div className={styles.fenSection}>
                <h4 className={styles.sectionTitle}>Board State</h4>
                <div className={styles.fenGrid}>
                  <div className={styles.fenRow}>
                    <span className={styles.fenLabel}>Before FEN:</span>
                    <code className={styles.fenValue}>{selectedAiResult.beforeFEN}</code>
                  </div>
                  <div className={styles.fenRow}>
                    <span className={styles.fenLabel}>After FEN:</span>
                    <code className={styles.fenValue}>{selectedAiResult.afterFEN}</code>
                  </div>
                </div>
              </div>

              {/* Validation Checks */}
              {selectedAiResult.validationChecks.length > 0 && (
                <div className={styles.stepsSection}>
                  <h4 className={styles.sectionTitle}>Validation Checks</h4>
                  <div className={styles.stepsList}>
                    {selectedAiResult.validationChecks.map((check, index) => (
                      <div
                        key={index}
                        className={`${styles.stepItem} ${check.passed ? styles.stepPassed : styles.stepFailed}`}
                      >
                        <StepIcon status={check.passed ? "passed" : "failed"} />
                        <span className={styles.stepName}>{check.name}</span>
                        {check.details && (
                          <span className={styles.stepMessage}>{check.details}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Firebase-specific fields */}
              {selectedAiResult._type === "ai-firebase" && (
                <div className={styles.mmrSection}>
                  <h4 className={styles.sectionTitle}>Firebase Sync</h4>
                  <table className={styles.mmrTable}>
                    <tbody>
                      <tr>
                        <td className={styles.mmrLabel}>Round Trips:</td>
                        <td className={styles.mmrValue}>{(selectedAiResult as AIFirebaseTestResult).firebaseRoundTrips}</td>
                      </tr>
                      <tr>
                        <td className={styles.mmrLabel}>Engines Consistent:</td>
                        <td className={`${styles.mmrValue} ${(selectedAiResult as AIFirebaseTestResult).enginesConsistent ? styles.positive : styles.negative}`}>
                          {(selectedAiResult as AIFirebaseTestResult).enginesConsistent ? "Yes" : "No"}
                        </td>
                      </tr>
                      <tr>
                        <td className={styles.mmrLabel}>Light Engine FEN:</td>
                        <td className={styles.mmrValue}><code>{(selectedAiResult as AIFirebaseTestResult).lightEngineFEN}</code></td>
                      </tr>
                      <tr>
                        <td className={styles.mmrLabel}>Dark Engine FEN:</td>
                        <td className={styles.mmrValue}><code>{(selectedAiResult as AIFirebaseTestResult).darkEngineFEN}</code></td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Piece ID Changes */}
                  {(selectedAiResult as AIFirebaseTestResult).pieceIdChanges.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <h5 className={styles.mmrSectionTitle}>Piece ID Changes (Firebase Round-Trip)</h5>
                      <table className={styles.deltaTable}>
                        <thead>
                          <tr>
                            <th>Piece</th>
                            <th>Before ID</th>
                            <th>After ID</th>
                            <th>Resolved By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedAiResult as AIFirebaseTestResult).pieceIdChanges.map((change, i) => (
                            <tr key={i}>
                              <td>{String(change.pieceType)}</td>
                              <td>{change.beforeId}</td>
                              <td>{change.afterId}</td>
                              <td className={change.resolvedBy !== "failed" ? styles.positive : styles.negative}>
                                {change.resolvedBy}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Sync Timings */}
                  {(selectedAiResult as AIFirebaseTestResult).syncTimings.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <h5 className={styles.mmrSectionTitle}>Sync Timings</h5>
                      <table className={styles.deltaTable}>
                        <thead>
                          <tr>
                            <th>Step</th>
                            <th>Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedAiResult as AIFirebaseTestResult).syncTimings.map((timing, i) => (
                            <tr key={i}>
                              <td>{timing.step}</td>
                              <td>{timing.durationMs.toFixed(1)}ms</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* State Consistency Checks */}
                  {(selectedAiResult as AIFirebaseTestResult).stateConsistencyChecks.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <h5 className={styles.mmrSectionTitle}>State Consistency</h5>
                      <table className={styles.deltaTable}>
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Light</th>
                            <th>Dark</th>
                            <th>Match</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedAiResult as AIFirebaseTestResult).stateConsistencyChecks.map((check, i) => (
                            <tr key={i}>
                              <td>{check.field}</td>
                              <td>{String(check.lightValue)}</td>
                              <td>{String(check.darkValue)}</td>
                              <td className={check.matches ? styles.positive : styles.negative}>
                                {check.matches ? "Yes" : "No"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Step-by-step execution results */}
              {selectedAiResult.steps && (
                <div className={styles.stepsSection}>
                  <h4 className={styles.sectionTitle}>Execution Steps</h4>
                  <div className={styles.stepsList}>
                    {selectedAiResult.steps.map((step, index) => (
                      <div
                        key={index}
                        className={`${styles.stepItem} ${styles[`step${step.status.charAt(0).toUpperCase() + step.status.slice(1)}`]}`}
                      >
                        <StepIcon status={step.status} />
                        <span className={styles.stepName}>{step.name}</span>
                        {step.message && (
                          <span className={styles.stepMessage}>{step.message}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error */}
              {selectedAiResult.error && (
                <div className={styles.errorSection}>
                  <h4 className={styles.sectionTitle}>Error</h4>
                  <div className={styles.errorMessage}>{selectedAiResult.error}</div>
                </div>
              )}

              {/* Timing */}
              <div className={styles.timingSection}>
                <span className={styles.timingLabel}>Duration:</span>
                <span className={styles.timingValue}>
                  {selectedAiResult.duration.toFixed(2)}ms
                </span>
              </div>
            </div>
          ) : selectedTestDef ? (
            <div className={styles.detailsPanel}>
              <h3 className={styles.detailsTitle}>
                {selectedTestDef.name}
                {selectedTestResult && (
                  <span
                    className={`${styles.statusBadge} ${
                      selectedTestResult.passed ? styles.badgePassed : styles.badgeFailed
                    }`}
                  >
                    {selectedTestResult.passed ? "PASSED" : "FAILED"}
                  </span>
                )}
              </h3>

              {selectedTestDef.description && (
                <p className={styles.testDescription}>{selectedTestDef.description}</p>
              )}

              {/* Step-by-step execution results */}
              {selectedTestResult?.steps && (
                <div className={styles.stepsSection}>
                  <h4 className={styles.sectionTitle}>Execution Steps</h4>
                  <div className={styles.stepsList}>
                    {selectedTestResult.steps.map((step, index) => (
                      <div
                        key={index}
                        className={`${styles.stepItem} ${styles[`step${step.status.charAt(0).toUpperCase() + step.status.slice(1)}`]}`}
                      >
                        <StepIcon status={step.status} />
                        <span className={styles.stepName}>{step.name}</span>
                        {step.message && (
                          <span className={styles.stepMessage}>{step.message}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sequence Steps (for multi-move tests) */}
              {selectedTestResult?.isSequence && selectedTestResult?.sequenceSteps && (
                <div className={styles.sequenceSection}>
                  <h4 className={styles.sectionTitle}>
                    Sequence Steps ({selectedTestResult.sequenceSteps.length} moves)
                  </h4>
                  <div className={styles.sequenceStepsList}>
                    {selectedTestResult.sequenceSteps.map((seqStep, index) => (
                      <div
                        key={index}
                        className={`${styles.sequenceStepItem} ${
                          seqStep.passed ? styles.sequenceStepPassed : styles.sequenceStepFailed
                        }`}
                      >
                        <div className={styles.sequenceStepHeader}>
                          <span className={styles.sequenceStepNumber}>Step {seqStep.stepNumber}</span>
                          <span className={styles.sequenceStepMove}>{seqStep.move}</span>
                          <span
                            className={`${styles.sequenceStepStatus} ${
                              seqStep.passed ? styles.passed : styles.failed
                            }`}
                          >
                            {seqStep.passed ? "✓" : "✗"}
                          </span>
                        </div>
                        {seqStep.note && (
                          <div className={styles.sequenceStepNote}>{seqStep.note}</div>
                        )}

                        {/* Status Effects - Expected and Actual */}
                        {(selectedTestDef.steps && selectedTestDef.steps[index]?.expectedStatus) || seqStep.actualStatus ? (
                          <div className={styles.sequenceStepStatusContainer}>
                            {/* Expected Status */}
                            {selectedTestDef.steps && selectedTestDef.steps[index]?.expectedStatus && (
                              <div className={styles.sequenceStepStatusSection}>
                                <span className={styles.sequenceStepLabel}>Expected Status:</span>
                                <div className={styles.sequenceStepStatusList}>
                                  {selectedTestDef.steps[index].expectedStatus!.length > 0 ? (
                                    selectedTestDef.steps[index].expectedStatus!.map((status, i) => (
                                      <div key={i} className={styles.sequenceStepStatusItem}>{status}</div>
                                    ))
                                  ) : (
                                    <div className={styles.sequenceStepStatusItem} style={{ opacity: 0.5 }}>No status effects</div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Actual Status */}
                            {seqStep.actualStatus !== undefined && (
                              <div className={styles.sequenceStepStatusSection}>
                                <span className={styles.sequenceStepLabel}>Actual Status:</span>
                                <div className={styles.sequenceStepStatusList}>
                                  {seqStep.actualStatus.length > 0 ? (
                                    seqStep.actualStatus.map((status, i) => (
                                      <div key={i} className={styles.sequenceStepStatusItem}>{status}</div>
                                    ))
                                  ) : (
                                    <div className={styles.sequenceStepStatusItem} style={{ opacity: 0.5 }}>No status effects</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : null}

                        <div className={styles.sequenceStepDetails}>
                          {/* FEN Comparison */}
                          <div className={styles.sequenceStepFEN}>
                            <span className={styles.sequenceStepLabel}>Expected FEN:</span>
                            <code className={styles.sequenceStepValue}>{seqStep.expectedFEN}</code>
                          </div>
                          <div className={styles.sequenceStepFEN}>
                            <span className={styles.sequenceStepLabel}>Actual FEN:</span>
                            <code
                              className={`${styles.sequenceStepValue} ${
                                seqStep.fenMatches ? styles.fenMatch : styles.fenMismatch
                              }`}
                            >
                              {seqStep.actualFEN}
                            </code>
                          </div>

                          {/* Delta Comparison Table */}
                          <div className={styles.sequenceStepDeltaTable}>
                            <h5 className={styles.deltaTableTitle}>Delta (What Changed)</h5>
                            <table className={styles.deltaTable}>
                              <thead>
                                <tr>
                                  <th></th>
                                  <th>Expected</th>
                                  <th>Actual</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className={styles.deltaKey}>Piece Moved:</td>
                                  <td className={styles.deltaValue}>
                                    {seqStep.expectedDelta?.pieceMoved || "-"}
                                  </td>
                                  <td
                                    className={`${styles.deltaValue} ${
                                      seqStep.expectedDelta?.pieceMoved !== seqStep.actualDelta?.pieceMoved
                                        ? styles.deltaMismatch
                                        : ""
                                    }`}
                                  >
                                    {seqStep.actualDelta?.pieceMoved || "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className={styles.deltaKey}>Capture:</td>
                                  <td className={styles.deltaValue}>
                                    {seqStep.expectedDelta?.pieceCaptured || "-"}
                                  </td>
                                  <td
                                    className={`${styles.deltaValue} ${
                                      seqStep.expectedDelta?.pieceCaptured !== seqStep.actualDelta?.pieceCaptured
                                        ? styles.deltaMismatch
                                        : ""
                                    }`}
                                  >
                                    {seqStep.actualDelta?.pieceCaptured || "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className={styles.deltaKey}>Status Changed:</td>
                                  <td className={styles.deltaValue}>
                                    {seqStep.expectedDelta?.statusChanged !== undefined
                                      ? String(seqStep.expectedDelta.statusChanged)
                                      : "-"}
                                  </td>
                                  <td
                                    className={`${styles.deltaValue} ${
                                      seqStep.expectedDelta?.statusChanged !== seqStep.actualDelta?.statusChanged
                                        ? styles.deltaMismatch
                                        : ""
                                    }`}
                                  >
                                    {seqStep.actualDelta?.statusChanged !== undefined
                                      ? String(seqStep.actualDelta.statusChanged)
                                      : "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className={styles.deltaKey}>Turn Changed:</td>
                                  <td className={styles.deltaValue}>
                                    {seqStep.expectedDelta?.turnChanged !== undefined
                                      ? String(seqStep.expectedDelta.turnChanged)
                                      : "-"}
                                  </td>
                                  <td
                                    className={`${styles.deltaValue} ${
                                      seqStep.expectedDelta?.turnChanged !== seqStep.actualDelta?.turnChanged
                                        ? styles.deltaMismatch
                                        : ""
                                    }`}
                                  >
                                    {seqStep.actualDelta?.turnChanged !== undefined
                                      ? String(seqStep.actualDelta.turnChanged)
                                      : "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className={styles.deltaKey}>Bonus Turn:</td>
                                  <td className={styles.deltaValue}>
                                    {seqStep.expectedDelta?.bonusTurn !== undefined
                                      ? String(seqStep.expectedDelta.bonusTurn)
                                      : "-"}
                                  </td>
                                  <td
                                    className={`${styles.deltaValue} ${
                                      seqStep.expectedDelta?.bonusTurn !== seqStep.actualDelta?.bonusTurn
                                        ? styles.deltaMismatch
                                        : ""
                                    }`}
                                  >
                                    {seqStep.actualDelta?.bonusTurn !== undefined
                                      ? String(seqStep.actualDelta.bonusTurn)
                                      : "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className={styles.deltaKey}>Game Ended:</td>
                                  <td className={styles.deltaValue}>
                                    {seqStep.expectedDelta?.gameEnded !== undefined
                                      ? String(seqStep.expectedDelta.gameEnded)
                                      : "-"}
                                  </td>
                                  <td
                                    className={`${styles.deltaValue} ${
                                      seqStep.expectedDelta?.gameEnded !== seqStep.actualDelta?.gameEnded
                                        ? styles.deltaMismatch
                                        : ""
                                    }`}
                                  >
                                    {seqStep.actualDelta?.gameEnded !== undefined
                                      ? String(seqStep.actualDelta.gameEnded)
                                      : "-"}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Game End Type - Only show if game actually ended */}
                          {(seqStep.expectedDelta?.gameEnded || seqStep.actualDelta?.gameEnded) && (
                            <div className={styles.gameEndSection}>
                              <h5 className={styles.gameEndTitle}>Game End Type</h5>
                              <table className={styles.deltaTable}>
                                <thead>
                                  <tr>
                                    <th>Field</th>
                                    <th>Expected</th>
                                    <th>Actual</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td className={styles.deltaKey}>Game End:</td>
                                    <td className={styles.deltaValue}>
                                      {seqStep.expectedResult?.gameEnd || "-"}
                                    </td>
                                    <td
                                      className={`${styles.deltaValue} ${
                                        seqStep.expectedResult?.gameEnd !== seqStep.actualResult?.gameEnd
                                          ? styles.deltaMismatch
                                          : ""
                                      }`}
                                    >
                                      {seqStep.actualResult?.gameEnd || "-"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className={styles.deltaKey}>Winner:</td>
                                    <td className={styles.deltaValue}>
                                      {seqStep.expectedResult?.winner || "-"}
                                    </td>
                                    <td
                                      className={`${styles.deltaValue} ${
                                        seqStep.expectedResult?.winner !== seqStep.actualResult?.winner
                                          ? styles.deltaMismatch
                                          : ""
                                      }`}
                                    >
                                      {seqStep.actualResult?.winner || "-"}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}

                          {seqStep.error && (
                            <div className={styles.sequenceStepError}>{seqStep.error}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FEN Comparison - Only show for non-sequence tests */}
              {!selectedTestResult?.isSequence && (
                <div className={styles.fenSection}>
                  <h4 className={styles.sectionTitle}>FEN Comparison</h4>
                  <div className={styles.fenGrid}>
                    <div className={styles.fenRow}>
                      <span className={styles.fenLabel}>Input FEN:</span>
                      <code className={styles.fenValue}>{selectedTestDef.inputFEN}</code>
                    </div>
                    <div className={styles.fenRow}>
                      <span className={styles.fenLabel}>Expected FEN:</span>
                      <code className={styles.fenValue}>{selectedTestDef.expectedFEN}</code>
                    </div>
                    {selectedTestResult && (
                      <div className={styles.fenRow}>
                        <span className={styles.fenLabel}>Actual FEN:</span>
                        <code
                          className={`${styles.fenValue} ${
                            selectedTestResult.fenMatches ? styles.fenMatch : styles.fenMismatch
                          }`}
                        >
                          {selectedTestResult.actualFEN || "(not run)"}
                        </code>
                      </div>
                    )}
                  </div>

                  {/* Piece Status Info (Runtime Properties like isShadow, isShadowed) */}
                  {selectedTestResult?.pieceStatusInfo && selectedTestResult.pieceStatusInfo.length > 0 && (
                    <div className={styles.pieceStatusSection}>
                      <h5 className={styles.pieceStatusTitle}>Runtime Piece Status (Not in FEN)</h5>
                      <ul className={styles.pieceStatusList}>
                        {selectedTestResult.pieceStatusInfo.map((info, idx) => (
                          <li key={idx} className={styles.pieceStatusItem}>{info}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Delta Information - Only show for non-sequence tests */}
              {!selectedTestResult?.isSequence && (
                <div className={styles.deltaSection}>
                <h4 className={styles.sectionTitle}>
                  Delta (What Changed)
                  {selectedTestResult && selectedTestDef.expectedDelta && (
                    <span className={`${styles.deltaMatchStatus} ${
                      selectedTestResult.deltaMatches ? styles.deltaMatch : styles.deltaMismatch
                    }`}>
                      {selectedTestResult.deltaMatches ? " ✓ Match" : " ✗ Mismatch"}
                    </span>
                  )}
                </h4>
                <div className={styles.deltaCompareGrid}>
                  {/* Header */}
                  <div className={styles.deltaHeader}>
                    <span className={styles.deltaHeaderLabel}></span>
                    <span className={styles.deltaHeaderValue}>Expected</span>
                    <span className={styles.deltaHeaderValue}>Actual</span>
                  </div>

                  {/* Piece Moved */}
                  <div className={styles.deltaCompareRow}>
                    <span className={styles.deltaLabel}>Piece Moved:</span>
                    <span className={styles.deltaValue}>
                      {selectedTestDef.expectedDelta?.pieceMoved || "-"}
                    </span>
                    <span className={`${styles.deltaValue} ${
                      selectedTestDef.expectedDelta?.pieceMoved !== undefined &&
                      selectedTestResult?.actualDelta?.pieceMoved !== selectedTestDef.expectedDelta.pieceMoved
                        ? styles.deltaMismatchValue : ""
                    }`}>
                      {selectedTestResult?.actualDelta?.pieceMoved || "-"}
                    </span>
                  </div>

                  {/* Capture */}
                  <div className={styles.deltaCompareRow}>
                    <span className={styles.deltaLabel}>Capture:</span>
                    <span className={styles.deltaValue}>
                      {selectedTestDef.expectedDelta?.pieceCaptured || "-"}
                    </span>
                    <span className={`${styles.deltaValue} ${
                      selectedTestDef.expectedDelta?.pieceCaptured !== undefined &&
                      selectedTestResult?.actualDelta?.pieceCaptured !== selectedTestDef.expectedDelta.pieceCaptured
                        ? styles.deltaMismatchValue : ""
                    }`}>
                      {selectedTestResult?.actualDelta?.pieceCaptured || "-"}
                    </span>
                  </div>

                  {/* Status Changed */}
                  <div className={styles.deltaCompareRow}>
                    <span className={styles.deltaLabel}>Status Changed:</span>
                    <span className={styles.deltaValue}>
                      {selectedTestDef.expectedDelta?.statusChanged !== undefined
                        ? selectedTestDef.expectedDelta.statusChanged ? "Yes" : "No"
                        : "-"}
                    </span>
                    <span className={`${styles.deltaValue} ${
                      selectedTestDef.expectedDelta?.statusChanged !== undefined &&
                      selectedTestResult?.actualDelta?.statusChanged !== selectedTestDef.expectedDelta.statusChanged
                        ? styles.deltaMismatchValue : ""
                    }`}>
                      {selectedTestResult?.actualDelta?.statusChanged !== undefined
                        ? selectedTestResult.actualDelta.statusChanged ? "Yes" : "No"
                        : "-"}
                    </span>
                  </div>

                  {/* Turn Changed */}
                  <div className={styles.deltaCompareRow}>
                    <span className={styles.deltaLabel}>Turn Changed:</span>
                    <span className={styles.deltaValue}>
                      {selectedTestDef.expectedDelta?.turnChanged !== undefined
                        ? selectedTestDef.expectedDelta.turnChanged ? "Yes" : "No"
                        : "-"}
                    </span>
                    <span className={`${styles.deltaValue} ${
                      selectedTestDef.expectedDelta?.turnChanged !== undefined &&
                      selectedTestResult?.actualDelta?.turnChanged !== selectedTestDef.expectedDelta.turnChanged
                        ? styles.deltaMismatchValue : ""
                    }`}>
                      {selectedTestResult?.actualDelta?.turnChanged !== undefined
                        ? selectedTestResult.actualDelta.turnChanged ? "Yes" : "No"
                        : "-"}
                    </span>
                  </div>

                  {/* Bonus Turn */}
                  <div className={styles.deltaCompareRow}>
                    <span className={styles.deltaLabel}>Bonus Turn:</span>
                    <span className={styles.deltaValue}>
                      {selectedTestDef.expectedDelta?.bonusTurn !== undefined
                        ? selectedTestDef.expectedDelta.bonusTurn ? "Yes" : "No"
                        : "-"}
                    </span>
                    <span className={`${styles.deltaValue} ${
                      selectedTestDef.expectedDelta?.bonusTurn !== undefined &&
                      selectedTestResult?.actualDelta?.bonusTurn !== selectedTestDef.expectedDelta.bonusTurn
                        ? styles.deltaMismatchValue : ""
                    }`}>
                      {selectedTestResult?.actualDelta?.bonusTurn !== undefined
                        ? selectedTestResult.actualDelta.bonusTurn ? "Yes" : "No"
                        : "-"}
                    </span>
                  </div>

                  {/* Game Ended */}
                  <div className={styles.deltaCompareRow}>
                    <span className={styles.deltaLabel}>Game Ended:</span>
                    <span className={styles.deltaValue}>
                      {selectedTestDef.expectedDelta?.gameEnded !== undefined
                        ? selectedTestDef.expectedDelta.gameEnded ? "Yes" : "No"
                        : "-"}
                    </span>
                    <span className={`${styles.deltaValue} ${
                      selectedTestDef.expectedDelta?.gameEnded !== undefined &&
                      selectedTestResult?.actualDelta?.gameEnded !== selectedTestDef.expectedDelta.gameEnded
                        ? styles.deltaMismatchValue : ""
                    }`}>
                      {selectedTestResult?.actualDelta?.gameEnded !== undefined
                        ? selectedTestResult.actualDelta.gameEnded ? "Yes" : "No"
                        : "-"}
                    </span>
                  </div>

                  {/* Status Effects */}
                  {(selectedTestDef.expectedDelta?.statusApplied?.length ||
                    selectedTestResult?.actualDelta?.statusApplied?.length) && (
                    <div className={styles.deltaCompareRow}>
                      <span className={styles.deltaLabel}>Status Effects:</span>
                      <span className={styles.deltaValue}>
                        {selectedTestDef.expectedDelta?.statusApplied?.join(", ") || "-"}
                      </span>
                      <span className={styles.deltaValue}>
                        {selectedTestResult?.actualDelta?.statusApplied?.join(", ") || "-"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Game End Result - Only show for non-sequence tests */}
              {!selectedTestResult?.isSequence && (
                <div className={styles.resultSection}>
                <h4 className={styles.sectionTitle}>Game End</h4>
                <div className={styles.resultGrid}>
                  <div className={styles.resultRow}>
                    <span className={styles.resultLabel}>Expected:</span>
                    <span className={styles.resultValue}>
                      {selectedTestDef.expectedResult?.gameEnd || "none"}
                    </span>
                  </div>
                  {selectedTestResult && (
                    <div className={styles.resultRow}>
                      <span className={styles.resultLabel}>Actual:</span>
                      <span
                        className={`${styles.resultValue} ${
                          selectedTestResult.resultMatches
                            ? styles.resultMatch
                            : styles.resultMismatch
                        }`}
                      >
                        {selectedTestResult.actualResult?.gameEnd || "none"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Error Message - Show detailed errors for sequence tests */}
              {selectedTestResult?.error && !selectedTestResult?.isSequence && (
                <div className={styles.errorSection}>
                  <h4 className={styles.sectionTitle}>Error</h4>
                  <div className={styles.errorMessage}>{selectedTestResult.error}</div>
                </div>
              )}

              {/* Timing */}
              {selectedTestResult && (
                <div className={styles.timingSection}>
                  <span className={styles.timingLabel}>Duration:</span>
                  <span className={styles.timingValue}>
                    {selectedTestResult.duration.toFixed(2)}ms
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.detailsPanel}>
              <div className={styles.emptyState}>
                <h3>Select a Test</h3>
                <p>Click on a test from the list to see details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestingPage;
